import { APPOINTMENT_EVENTS } from '@/components/appointment/consts.ts'
import { Dollar, Euro, __t } from './helpers'

import otherIcon from '@/assets/images/payment-types/other.svg'
import giftcardIcon from '@/assets/images/payment-types/giftcard.svg'
import packageIcon from '@/assets/images/payment-types/package.svg'
import membershipIcon from '@/assets/images/payment-types/membership.svg'
import splitIcon from '@/assets/images/payment-types/splitpayment.svg'
import mobilepaymentIcon from '@/assets/images/payment-types/mobilepayment.svg'
import cashIcon from '@/assets/images/payment-types/cash.svg'
import creditcardIcon from '@/assets/images/payment-types/creditcard.svg'
import paypalIcon from '@/assets/images/payment-types/paypal.svg'
import checkIcon from '@/assets/images/payment-types/check.svg'
import squareIcon from '@/assets/images/payment-types/square.svg'
import stripeTerminalIcon from '@/assets/images/payment-types/stripe_terminal.svg'
import tapToPayIcon from '@/assets/images/payment-types/tap-to-pay.svg'
import booksyGiftCardIcon from '@/assets/images/payment-types/booksy-gift-card.svg'
import keyedInPaymentIcon from '@/assets/images/payment-types/keyed-in-payment.svg'

export const COUNTRY_NAMES = Object.freeze({
  za: 'RSA',
  ar: 'Argentina',
  br: 'Brasil',
  cl: 'Chile',
  ca: 'Canada',
  mx: 'México',
  us: 'United States of America',
  au: 'Australia',
  es: 'España',
  ie: 'Ireland',
  pl: 'Polska',
  pt: 'Portugal',
  gb: 'United Kingdom',
  se: 'Sweden',
  de: 'Germany',
  ru: 'Russia',
  it: 'Italy',
  nl: 'Netherlands',
  ng: 'Nigeria',
  in: 'India',
  co: 'Colombia',
  my: 'Malaysia',
})

export const SUPPORTED_COUNTRIES = Object.freeze(Object.keys(COUNTRY_NAMES))

export const API_KEYS = {
  www: {
    live: 'frontdesk-76661e2b-25f0-49b4-b33a-9d78957a58e3',
    dev: 'frontdesk-e6edc8f2-7dc8-40e1-9f39-29a2e186d3cf',
  },
  ios: {
    live: 'frontdesk-ios-t-juotcun-44k96q-39xotr9jfr-xkvqpc5',
    dev: 'frontdesk-ios-t-ojkevd-hyrcpyaru-gnzwpuxfb9-fdk4c',
  },
  android: {
    live: 'frontdesk-and-t-byehw-7ujd9eq5-76ymto1vh-ts9td3w3',
    dev: 'frontdesk-and-t-mv5h3vzu-o3fth9c-c5tx6b3d-mgu6eea',
  },
  ios_mobile: {
    live: 'iphone-b30-e8645e85-bd0c-44b7-a449-1705cc5e3652',
    dev: 'iphone-b30-e1b283c9-fc1f-40b0-9932-ec07260d1b33',
  },
  android_mobile: {
    live: 'android-b30-dd967194-0b70-4a4c-a095-bdcde0377033',
    dev: 'android-b30-bc67fc44-e7e9-4694-aaf7-5d54381138df',
  },
}

export const APP_DEEPLINK = 'https://dl.booksy.com/izQdExw5Kgb'

export const DEV_CONSOLE_CLICKS = 10

export const EXPERIMENTS = {
  Experiment_ChurnPauseButton: 'Experiment_ChurnPauseButton',
  Experiment_ShareLoyaltyProgram: 'Experiment_ShareLoyaltyProgram',
  Experiment_OnboardingKYC: 'Experiment_OnboardingKYC',
  Experiment_SMSMarketingConsentUS: 'Experiment_SMSMarketingConsentUS',
  Experiment_PromoteDepositsOverCFV2: 'Experiment_PromoteDepositsOverCFV2',
  Experiment_ShareBroaderBeautyProfile: 'Experiment_ShareBroaderBeautyProfile',
  Experiment_CreateClientGroup: 'Experiment_CreateClientGroup',
  Experiment_GoogleReviewsImportProfileTab: 'Experiment_GoogleReviewsImportProfileTab',
  Experiment_NailsServiceOnboarding: 'Experiment_NailsServiceOnboarding',
  Experiment_RegistrationServicesImages: 'Experiment_RegistrationServicesImages',
  Experiment_OnboardingKYCInPoland: 'Experiment_OnboardingKYCInPoland',
  Experiment_SubscriptionKYCInPoland: 'Experiment_SubscriptionKYCInPoland',
  Experiment_AirLiteOnboarding: 'Experiment_AirLiteOnboarding',
  Experiment_BooksyAssistantChat: 'Experiment_BooksyAssistantChat',
  Experiment_BooksyAssistantForExistingProviders: 'Experiment_BooksyAssistantForExistingProviders',
  Experiment_ProfileSubscriptionStatusBadge: 'Experiment_ProfileSubscriptionStatusBadge',
  Experiment_OnboardingChecklist: 'Experiment_OnboardingChecklist',

  // - Permanent AA Experiments Flags
  // These flags must remain in the codebase permanently.
  // Purpose: Supports recurring AA tests (3–4 times/year) to:
  //   - Monitor Eppo’s calculation of natural metric variance (for better sample size estimation)
  //   - Debug experiment event flows quickly
  //   - Detect if Eppo ever incorrectly calls a winner in an AA test due to random variance
  // Do NOT remove or rename this flag during routine cleanup or refactoring.
  // Stakeholder: Experimentation Lead
  Experiment_AAtestMRS: 'Experiment_AAtestMRS',
}

export const FEATURE_FLAGS = {
  // Flags added permanently and kill switches
  Maintenance_Mode: 'Maintenance_Mode',
  // end Flags added permanently and kill switches

  // PxOnboarding Feature Flags
  Feature_PublicBusinessEmail: 'Feature_PublicBusinessEmail',
  Feature_BarbersShareProfileAfterInvite:
    'Feature_BarbersShareProfileAfterInvite',
  Feature_SettingsGooglePlacesV3: 'Feature_SettingsGooglePlacesV3',
  Feature_RegistrationGooglePlacesV3: 'Feature_RegistrationGooglePlacesV3.0',
  Feature_GoogleAddressManualFallback: 'Feature_GoogleAddressManualFallback',
  Feature_BusinessPhoneVerificationFlag: 'Feature_BusinessPhoneVerificationFlag',
  Feature_SMSMarketingConsentUS: 'Feature_SMSMarketingConsentUS',
  Feature_AccountDeletionViews: 'Feature_AccountDeletionViews',
  Feature_FrontdeskGoogleMaps: 'Feature_FrontdeskGoogleMaps',
  Feature_GoogleReviewsBadge: 'Feature_GoogleReviewsBadge',
  // end PxOnboarding Feature Flags

  // PxFinanceAutomation Feature Flags
  Feature_ForceConfirmBillingInvoiceDataFlag:
    'Feature_ForceConfirmBillingInvoiceDataFlag',
  Fix_SubscriptionStripePaymentIntentStatusCheck:
    'Fix_SubscriptionStripePaymentIntentStatusCheck',
  // end PxFinanceAutomation Feature Flags

  // PxMarketing Feature Flags
  PortfolioInstagram: 'Feature_PortfolioInstagram', // Permanent flag
  Feature_ReferAFriend: 'Feature_ReferAFriend', // Permanent flag
  Feature_ShowSettingsReferralBanner: 'Feature_ShowSettingsReferralBanner',
  Feature_ShowNotificationsReferralBanner: 'Feature_ShowNotificationsReferralBanner',
  Feature_BlackFridayBanner: 'Feature_BlackFridayBanner', // Permanent flag
  Feature_ValentinesDayBanner: 'Feature_ValentinesDayBanner', // Permanent flag
  // end PxMarketing Feature Flags

  // PxCalendar - firestore integration
  Feature_ProviderFirestore: 'Feature_ProviderFirestore',
  Feature_CalendarFirestoreIntegration: 'Feature_CalendarFirestoreIntegration',
  // end PxCalendar - firestore integration

  // PxCalendar - AI Receptionist
  Feature_AiReceptionist: 'Feature_AiReceptionist',
  Feature_AirSkillsAndForwardingInstructions: 'Feature_AirSkillsAndForwardingInstructions',
  // end PxCalendar - AI Receptionist

  // PxEngagement Feature Flags
  Feature_IterableEmbeddedMessaging: 'Feature_IterableEmbeddedMessaging',
  Feature_InTouchChat: 'Feature_InTouchChat',
  Logging_Booksy_Unresponsive_Message: 'Logging_Booksy_Unresponsive_Message',
  LoyaltyProgram: 'Feature_LoyaltyProgram',
  Feature_ShowBadgeNewOnLoyaltyProgram: 'Feature_ShowBadgeNewOnLoyaltyProgram',
  Feature_PackagesOnBusinessProfile: 'Feature_PackagesOnBusinessProfile',
  // end PxEngagement Feature Flags

  Feature_BooksyWrapped: 'Feature_BooksyWrapped',
  Feature_BoostScore: 'Feature_BoostScore',
  Feature_BoostClaimCopyChanges: 'Feature_BoostClaimCopyChanges',
  Feature_BoostAppointmentBlockClientDeleting:
    'Feature_BoostAppointmentBlockClientDeleting',
  Feature_TurnTracker: 'Feature_TurnTracker',
  Feature_TurnTrackerCheckInMode: 'Feature_TurnTrackerCheckInMode',
  Feature_Ecommerce: 'Feature_Ecommerce',
  Feature_Ecommerce_Modal: 'Feature_Ecommerce_Modal',
  Feature_ProductSuggestionsModal: 'Feature_EcommerceProductSuggestionsModal',
  Feature_QuickCardEntryOnCheckout: 'Feature_QuickCardEntryOnCheckout',
  Feature_EffortlessKYCAfterEkranZBaba: 'Feature_EffortlessKYCAfterEkranZBaba',
  Refactor_CustomConsentsKycMicroservice:
    'Refactor_CustomConsentsKycMicroservice',
  Feature_StorePermissions: 'feature_resource_store_permission',
  Feature_NewInvoicingProcessIE: 'Feature_NewInvoicingProcessIE',
  Feature_NethoneEnabled: 'Feature_NethoneEnabled',
  Feature_PeakHours: 'Feature_PeakHours',
  Feature_BooksyPayCancellationFeeWindowUnified: 'Feature_BooksyPayCancellationFeeWindowUnified',
  Feature_NSPFixedPrize: 'Feature_NSPFixedPrize',
  Feature_WebinarInfobox: 'Feature_WebinarInfobox',
  Feature_VisibilityPromotionMVP: 'Feature_VisibilityPromotionMVP',
  Feature_PlatformFee: 'Feature_PlatformFee',
  Feature_PriceTiers: 'Feature_PriceTiers',
  Feature_VisibilityPromotionMVPStats: 'Feature_VisibilityPromotionMVPStats',
  Feature_UseNewTransactionDetailsGet: 'Feature_UseNewTransactionDetailsGet',
  Feature_SwitchBoostToVisibilityFrontend: 'Feature_SwitchBoostToVisibilityFrontend',
  Feature_AppointmentPaymentDetailsFromBasket: 'Feature_AppointmentPaymentDetailsFromBasket',
  Feature_TipTileInApptForm: 'Feature_TipTileInApptForm',
  Feature_InspirationPhotoApptDetails: 'Feature_InspirationPhotoApptDetails',
  Feature_BooksyMedFamilyAppointmentButton: 'Feature_BooksyMedFamilyAppointmentButton',
  Feature_BooksyMedFamilyV2RelationsFlag: 'Feature_BooksyMedFamilyV2RelationsFlag',
  Feature_AllowPaymentsForTrialBusiness: 'Feature_AllowPaymentsForTrialBusiness',
  Feature_LockTrialMessageBlastTemplates: 'Feature_LockTrialMessageBlastTemplates',
  Feature_TaxIdFieldKYCOnboarding: 'Feature_TaxIdFieldKYCOnboarding',
  Feature_ShowNSPSettingsCardVerificationInfo: 'Feature_ShowNSPSettingsCardVerificationInfo',
  Experiment_NailsServiceOnboarding: 'Experiment_NailsServiceOnboarding',
  Verifactu_due_invoicing_changes: 'Verifactu_due_invoicing_changes',
  Feature_StandaloneTipNotificationToggle: 'Feature_StandaloneTipNotificationToggle',
  Feature_TippingModalCounterDate: 'Feature_TippingModalCounterDate',
  Feature_BcrS700Enable: 'Feature_BcrS700Enable',
}

export const ENDPOINTS = {
  /* ROUTE */
  ROUTE_CONFIG: '/config',
  ROUTE_ENVIRONMENTS: '/route/environments',

  /*! used in: `~~/store/api.js` */
  CUSTOMERS: '/business_api/me/businesses/:businessId/customers',
  CUSTOMER_TO_MERGE: '/business_api/me/businesses/:businessId/customers/merge',
  BUSINESS_CUSTOMER:
    '/business_api/me/businesses/:businessId/customers/:customerId',
  BUSINESS_TAGS: '/business_api/me/businesses/:businessId/customers/tags',
  FAMILY_FRIENDS: '/business_api/me/businesses/:businessId/customers/:customerId/family',
  FAMILY_FRIENDS_CREATE: '/business_api/me/businesses/:businessId/family_and_friends/members/',

  ROUTING_CONFIG: '/routing/config',
  ACCOUNT_LOGIN: '/business_api/account/login',
  ACCOUNT_LOGOUT: '/business_api/account/logout',
  ACCOUNT_ME: '/business_api/me',
  ACCOUNT_LOGIN_FB: '/business_api/account/login/facebook/',
  ACCOUNT_LOGIN_APPLE: '/business_api/account/login/apple/',
  ACCOUNT_LOGIN_GOOGLE: '/business_api/account/login/google/one_tap/',
  ACCOUNT_DELETION: '/business_api/me/account_deletion/',
  GET_AUTH_TOKEN: '/business_api/me/auth_token',
  AUTH_TOKEN: '/business_api/account/login/auth_token',
  USER_BUSINESSES: '/business_api/me/businesses',
  ACCOUNT_EXISTS: '/business_api/account/exists',
  REFERRAL_CODE: '/business_api/referral_code/:code',
  AGREEMENTS: '/business_api/me/businesses/business_agreements',
  EXPERIMENTS: '/experiment/:experimentName/experiment_variant/:relationId/',
  EXPERIMENTS_BUSINESS:
    '/business_api/me/businesses/:businessId/experiment_variant/:experimentName/',

  CONNECT_WITH_FACEBOOK: '/business_api/me/connect/',
  CONNECT_WITH_GOOGLE: '/business_api/me/connect/google/',

  BUSINESS: '/business_api/me/businesses/:businessId',
  BUSINESS_AGREEMENTS: '/business_api/me/businesses/:businessId/agreements',
  BUSINESS_PHOTO: '/business_api/me/businesses/:businessId/customers/photo',
  BUSINESS_VISIBILITY: '/business_api/me/businesses/:businessId/visibility/',
  RESOURCES: '/business_api/me/businesses/:businessId/resources',
  RESOURCE_ID: '/business_api/me/resources/:resourceId',
  RESOURCE_PHOTO: '/business_api/me/resources/photo',
  RESOURCE_INVITE: '/business_api/me/resources/:resourceId/invite',
  BUSINESS_ACCOUNT_DELETION:
    '/business_api/me/businesses/:businessId/account_deletion',
  BUSINESS_SERVICE_COLOR_PALETTE: '/business_api/me/color_palette',
  BUSINESS_OPENING_HOURS:
    '/business_api/me/businesses/:businessId/business_opening_hours/',

  /* CUSTOMER */
  CUSTOMER_AGREEMENTS:
    '/business_api/me/businesses/:businessId/customer_agreements',
  CUSTOMER_GROUPS: '/business_api/me/businesses/:businessId/customer_groups',
  CUSTOMER_BOOKINGS:
    '/business_api/me/businesses/:businessId/customers/:customerId/bookings',
  CUSTOMER_FILES:
    '/business_api/me/businesses/:businessId/customers/:customerId/attached_files',
  CUSTOMER_ATTACHED_FILE:
    '/business_api/me/businesses/:businessId/customers/:customerId/attached_files/:attachedFileId',
  CUSTOMER_ATTACHED_FILE_DOWNLOAD:
    '/business_api/me/businesses/:businessId/customers/:customerId/attached_files/:attachedFileId/download/',
  CUSTOMER_PHOTOS:
    '/business_api/me/businesses/:businessId/customers/:customerId/more_photos',
  CUSTOMER_EXISTS: '/business_api/me/businesses/:businessId/customer/exists/',
  EMAIL_TEMPLATES: '/business_api/me/businesses/:businessId/email_templates',
  SMS_INVITE:
    '/business_api/me/businesses/:businessId/sms/send/invite_customers',
  BUSINESS_CATEGORIES: '/business_api/me/businesses/:businessId/categories',
  NOTIFICATION_TEXT:
    '/business_api/me/businesses/:businessId/get_notification_text',
  NOTIFICATION_TEMPLATES:
    '/business_api/me/businesses/:businessId/notification_templates/',
  CUSTOMER_BUYERS:
    '/business_api/me/businesses/:businessId/customers/:customerId/buyers/',
  CUSTOMER_UNLOCK_FIELD:
    '/business_api/me/businesses/:businessId/customers/:customerId/locked',
  CUSTOMERS_BUYERS: '/business_api/me/businesses/:businessId/customers/buyers/',
  BOOKING_COUNTERS:
    '/business_api/me/businesses/:businessId/segment/booking_counters/',
  CUSTOMER_PRODUCTS:
    '/business_api/me/businesses/:businessId/customers/:customerId/products',
  CUSTOMER_LINK:
    '/business_api/me/businesses/:businessId/customers/:customerId/link',
  CUSTOMER_UNLINK:
    '/business_api/me/businesses/:businessId/customers/:customerId/unlink',

  PHYSIOTHERAPY_PATIENT_FILE:
    '/business_api/me/businesses/:businessId/patient_file_fields_with_options/',

  /*! search */
  STREET_HINTS: '/business_api/search/street_hints',
  STREET_HINTS_RESOLVE: '/business_api/search/street_hints/resolve',
  REGION_HINTS: '/business_api/region_hints/',
  SEARCH_VENUE: '/business_api/search/venue_street/',

  /*! reverse geocode */
  REVERSE_GEOCODE: '/utils/reverse_geocode/',

  /*! calendar */
  BUSINESS_CALENDAR: '/business_api/me/businesses/:businessId/calendar',
  BUSINESS_CALENDAR_MONTHLY:
    '/business_api/me/businesses/:businessId/monthly_bookings',
  SERVER_TIME: '/routing/server_time/',
  NEXT_STAFFER:
    '/business_api/me/businesses/:businessId/calendar/next_staffer/',

  /*! calendar change */
  BUSINESS_CALENDAR_CHANGE_DATES:
    '/business_api/me/businesses/:businessId/calendar/changed_dates/:timestamp/',

  /*! categories with services */
  SERVICE_CATEGORIES:
    '/business_api/me/businesses/:businessId/service_categories',
  SERVICE_CATEGORY:
    '/business_api/me/businesses/:businessId/service_categories/:serviceCategoryId',
  CATEGORY_DELETE:
    '/business_api/me/businesses/:businessId/service_categories/:serviceCategoryId',
  /*! services */
  SERVICES: '/business_api/me/businesses/:businessId/services',
  SERVICES_BULK: '/business_api/me/businesses/:businessId/services/bulk',
  SERVICES_BULK_FROM_URL:
    '/business_api/me/businesses/:businessId/services/bulk/from_url',
  SERVICE: '/business_api/me/businesses/:businessId/services/:serviceId',
  SERVICE_PHOTO_UPLOAD:
    '/business_api/me/businesses/:businessId/services/:serviceId/photo',
  SERVICE_PHOTO_DELETE:
    '/business_api/me/businesses/:businessId/services/:serviceId/photo/:photoId/',
  SERVICE_PHOTO_UPDATE:
    '/business_api/me/businesses/:businessId/services/:serviceId/photo/:photoId/',
  SERVICE_USAGE:
    '/business_api/me/businesses/:businessId/services/:serviceId/usage/',
  SERVICES_SUGGESTION:
    '/business_api/me/businesses/:businessId/service_suggestion/',
  SERVICES_SUGGESTION_2:
    '/business_api/me/businesses/:businessId/suggest_services/',
  SERVICES_HINTS: '/business_api/me/businesses/:businessId/service_hints',
  SERVICES_ADDONS: '/business_api/me/businesses/:businessId/services/addons',
  SERVICE_ADDONS:
    '/business_api/me/businesses/:businessId/services/:serviceId/addons',
  SERVICES_ADDONS_EDIT:
    '/business_api/me/businesses/:businessId/services/addons/:addOnId',
  SERVICES_ADDONS_UPLOAD_PHOTO:
    '/business_api/me/businesses/:businessId/services/addons/:addOnId/photo',

  SERVICE_TYPES: '/business_api/service_types/',
  SERVICE_TYPE_SUGGESTIONS:
    '/business_api/me/businesses/:businessId/services/suggest_service_types/',
  SERVICE_TYPE_ASSIGN:
    '/business_api/me/businesses/:businessId/services/assign_treatment/',

  /* POS */
  POS: '/business_api/me/businesses/:businessId/pos/',
  POS_BOOKING_CHARGES:
    '/business_api/me/businesses/:businessId/pos/booking_charges',
  POS_PRODUCT_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/product_categories',
  POS_TRANSACTIONS: '/business_api/me/businesses/:businessId/pos/transactions',
  POS_TRANSACTION:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId',
  POS_TRANSACTION_LAST_RECEIPT:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/last_receipt',
  POS_TRANSACTION_SEND_RECEIPT:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/send_receipt',
  POS_TRANSACTION_CREATE_INVOICE:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/create_invoice',
  POS_TRANSACTION_ACTION:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/action',
  POS_TRANSACTION_PRINT_RECEIPT:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/print_receipt',
  POS_TRANSACTION_BSX_RECEIPT:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/bsx',
  POS_TRANSACTION_BSX_LOG:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/bsx/log',
  POS_TRANSACTION_SPLASH:
    '/business_api/me/businesses/:businessId/pos/transactions/:transactionId/splash',
  POS_BSX_FILE_LINK:
    '/business_api/me/businesses/:businessId/pos/bsx_file_link/',
  POS_SEARCH_ITEMS: '/business_api/me/businesses/:businessId/pos/search_items',
  POS_REGISTERS: '/business_api/me/businesses/:businessId/pos/registers',
  POS_REGISTERS_ID:
    '/business_api/me/businesses/:businessId/pos/registers/:registerId',
  POS_REGISTERS_ID_OPERATIONS:
    '/business_api/me/businesses/:businessId/pos/registers/:registerId/operations',
  POS_REGISTERS_ID_DOWNLOAD:
    '/business_api/me/businesses/:businessId/pos/registers/:registerId/direct_download/',
  POS_REGISTERS_ID_CLOSE:
    '/business_api/me/businesses/:businessId/pos/registers/:registerId/close/',
  POS_REGISTERS_ID_REOPEN:
    '/business_api/me/businesses/:businessId/pos/registers/:registerId/reopen/',
  POS_PLAN_INFO: '/business_api/me/businesses/:businessId/pos/pos_plan_info/',
  POS_PAYMENT_ROWS: '/business_api/me/businesses/:businessId/pos/payment_rows',
  POS_PAYMENT_ROWS_SUMMARY:
    '/business_api/me/businesses/:businessId/pos/payment_rows_summary',
  POS_APPLY_TAX_RATE_FOR_SERVICES:
    '/business_api/me/businesses/:businessId/pos/apply_tax_rate/services/',
  POS_APPLY_TAX_RATE_FOR_PRODUCTS:
    '/business_api/me/businesses/:businessId/pos/apply_tax_rate/products/',
  POS_FAST_PAYOUT_ENABLED_BY_MERCHANT:
    '/business_api/me/businesses/:businessId/pos/fast_payout_enabled_by_merchant/',
  POS_PAY_BY_APP_INFO:
    '/business_api/me/businesses/:businessId/pos/pay_by_app_info/',

  /* STRIPE TERMINAL */
  STRIPE_KYC_LINK:
    '/business_api/me/businesses/:businessId/stripe/account_link',
  STRIPE_AVAILABLE_BALANCE:
    '/business_api/me/businesses/:businessId/stripe/available_balance/',
  STRIPE_ACCOUNT: '/business_api/me/businesses/:businessId/stripe/account/',
  STRIPE_CONNECTION_TOKEN:
    '/business_api/me/businesses/:businessId/stripe/connection_token',
  STRIPE_FAST_PAYOUT_CREATE:
    '/business_api/me/businesses/:businessId/stripe/payouts/fast/',
  STRIPE_PAYMENT_INTENT_CREATE:
    '/business_api/me/businesses/:businessId/stripe/payment_intent',
  STRIPE_PAYMENT_INTENT_ACTION:
    '/business_api/me/businesses/:businessId/stripe/payment_intent/:paymentIntentId/action',
  STRIPE_SDI_PROCESS_PAYMENT:
    '/business_api/me/businesses/:businessId/stripe_sdi/terminal/:readerId/payment_intent/:paymentIntentId/process/',
  STRIPE_PAYMENT_INTENT_STATUS:
    '/business_api/me/businesses/:businessId/stripe_sdi/payment_intent/:paymentIntentId/status/',
  STRIPE_SDI_CANCEL_PAYMENT:
    '/business_api/me/businesses/:businessId/stripe_sdi/terminal/:readerId/action/cancel/',
  STRIPE_PAYOUT_ACCOUNT_DETAILS:
    '/business_api/me/businesses/:businessId/stripe/payout_account_details/',
  STRIPE_PAYOUT_LIMITS:
    '/business_api/me/businesses/:businessId/pos/fast_payout_limit/',
  STRIPE_READER: '/business_api/me/businesses/:businessId/stripe/reader/',
  STRIPE_READERS: '/business_api/me/businesses/:businessId/stripe/readers/',
  STRIPE_TERMINAL_HARDWARE:
    '/business_api/me/businesses/:businessId/stripe/terminal/hardware/',
  STRIPE_READER_LABEL:
    '/business_api/me/businesses/:businessId/stripe/reader/:readerId/label/',
  STRIPE_SDI_MODE_CHANGE:
    '/business_api/me/businesses/:businessId/stripe_sdi/integration_mode/change/',

  /* STRIPE MOBILE PAYMENTS */
  STRIPE_PAYMENTS_STRIPE_ACCOUNT:
    '/business_api/me/businesses/:businessId/payments/account/stripe/',
  STRIPE_PAYMENTS_ACCOUNT_DETAILS:
    '/business_api/me/businesses/:businessId/payments/account_details/stripe/',
  STRIPE_CONFIG: '/business_api/me/businesses/:businessId/stripe/config/',
  STRIPE_PAYOUT_METHODS:
    '/business_api/me/businesses/:businessId/payments/account_details/stripe/payout_methods/',
  STRIPE_SET_DEFAULT_PAYOUT:
    '/business_api/me/businesses/:businessId/payments/account_details/stripe/payout_method/:token/set_as_default/:payoutType/',
  STRIPE_DELETE_PAYOUT_METHOD:
    '/business_api/me/businesses/:businessId/payments/account_details/stripe/payout_method/:token/',
  STRIPE_PAYMENTS_BANNERS:
    '/business_api/me/businesses/:businessId/payments/banners/',

  /* POS COMMISSIONS */
  POS_COMMISSION_DEFAULTS:
    '/business_api/me/businesses/:businessId/pos/commissions',
  POS_COMMISSION_DEFAULTS_RESOURCE_ID:
    '/business_api/me/businesses/:businessId/pos/commissions/resource/:resourceId',
  /* POS COMMISSIONS CATEGORIES */
  POS_COMMISSION_SERVICES_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/commissions/product_categories/service_variants',
  POS_COMMISSION_PRODUCTS_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/commissions/product_categories/products',
  POS_COMMISSION_EGIFTS_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/commissions/product_categories/egifts',
  POS_COMMISSION_MEMBERSHIPS_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/commissions/product_categories/memberships',
  POS_COMMISSION_PACKAGES_CATEGORIES:
    '/business_api/me/businesses/:businessId/pos/commissions/product_categories/packages',

  POS_COMMISSION_SERVICE_VARIANTS:
    '/business_api/me/businesses/:businessId/pos/commissions/service_variants',
  POS_COMMISSION_CHANGE_SERVICE_VARIANTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/service_variants/',
  POS_COMMISSION_CHANGE_RESOURCE_SERVICE_VARIANTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/resources/:resourceId/service_variants/',
  POS_COMMISSION_PRODUCTS:
    '/business_api/me/businesses/:businessId/pos/commissions/products',
  POS_COMMISSION_CHANGE_PRODUCTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/products/',
  POS_COMMISSION_CHANGE_RESOURCE_PRODUCTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/resources/:resourceId/products/',
  POS_COMMISSION_PACKAGES:
    '/business_api/me/businesses/:businessId/pos/commissions/packages',
  POS_COMMISSION_CHANGE_PACKAGES:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/packages/',
  POS_COMMISSION_CHANGE_RESOURCE_PACKAGES:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/resources/:resourceId/packages/',
  POS_COMMISSION_MEMBERSHIPS:
    '/business_api/me/businesses/:businessId/pos/commissions/memberships',
  POS_COMMISSION_CHANGE_MEMBERSHIPS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/memberships/',
  POS_COMMISSION_CHANGE_RESOURCE_MEMBERSHIPS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/resources/:resourceId/memberships/',
  POS_COMMISSION_EGIFTS:
    '/business_api/me/businesses/:businessId/pos/commissions/egifts',
  POS_COMMISSION_CHANGE_EGIFTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/egifts/',
  POS_COMMISSION_CHANGE_RESOURCE_EGIFTS:
    '/business_api/me/businesses/:businessId/pos/commissions/mass/resources/:resourceId/egifts/',

  /* INVOICING */
  INVOICING_BUYERS: '/business_api/me/businesses/:businessId/invoicing/buyers',
  INVOICING_BUYERS_EDIT:
    '/business_api/me/businesses/:businessId/invoicing/buyers/:buyerId',
  INVOICING_CASH_REGISTER_DOCUMENT:
    '/business_api/me/businesses/:businessId/invoicing/cash_register_document',
  INVOICING_CASH_REGISTER_DOCUMENT_ID:
    '/business_api/me/businesses/:businessId/invoicing/cash_register_document/:documentId',
  INVOICING_CASH_REGISTER_DOCUMENT_ID_PREVIEW:
    '/business_api/me/businesses/:businessId/invoicing/cash_register_document/:documentId/preview',
  INVOICES: '/business_api/me/businesses/:businessId/invoicing/invoice/',
  INVOICING_INVOICE_ID:
    '/business_api/me/businesses/:businessId/invoicing/invoice/:invoiceId',
  INVOICING_INVOICE_ID_CORRECTION:
    '/business_api/me/businesses/:businessId/invoicing/invoice/:invoiceId/correction',
  INVOICING_INVOICE_ID_SEND:
    '/business_api/me/businesses/:businessId/invoicing/invoice/:invoiceId/send_to_buyer',
  INVOICING_INVOICE_ID_PDF:
    '/business_api/me/businesses/:businessId/invoicing/invoice/:invoiceId/preview',
  INVOICING_SELLER: '/business_api/me/businesses/:businessId/invoicing/seller/',

  /*! warehouse */
  WAREHOUSE_COMMODITIES:
    '/business_api/me/businesses/:businessId/warehouse/commodities',
  WAREHOUSE_COMMODITY:
    '/business_api/me/businesses/:businessId/warehouse/commodity',
  WAREHOUSE_COMMODITY_ARCHIVE:
    '/business_api/me/businesses/:businessId/warehouse/commodity/archive',
  WAREHOUSE_COMMODITY_DELETE:
    '/business_api/me/businesses/:businessId/warehouse/commodity/delete',
  WAREHOUSE_COMMODITY_CONSUMPTION:
    '/business_api/me/businesses/:businessId/warehouse/commodity/consumption',
  WAREHOUSE_COMMODITIES_BY_WAREHOUSE:
    '/business_api/me/businesses/:businessId/warehouse/warehouses/:warehouseId/commodities/',
  WAREHOUSE_COMMODITIES_SEARCH:
    '/business_api/me/businesses/:businessId/warehouse/commodities/search',
  WAREHOUSE_COMMODITY_CATEGORIES:
    '/business_api/me/businesses/:businessId/warehouse/categories',
  WAREHOUSE_COMMODITY_CATEGORY:
    '/business_api/me/businesses/:businessId/warehouse/category',
  WAREHOUSE_DOCUMENTS:
    '/business_api/me/businesses/:businessId/warehouse/documents',
  WAREHOUSE_MEASURES:
    '/business_api/me/businesses/:businessId/warehouse/measures',
  WAREHOUSE_WAREHOUSES:
    '/business_api/me/businesses/:businessId/warehouse/warehouses',
  WAREHOUSE_EXPENDITURE_REASONS: `/business_api/me/businesses/:businessId/warehouse/internal_expenditure_reasons/`,
  WAREHOUSE_SUPPLIER: `/business_api/me/businesses/:businessId/warehouse/supplier/`,
  WAREHOUSE_SUPPLIER_ID: `/business_api/me/businesses/:businessId/warehouse/supplier/:supplierId`,
  WAREHOUSE_BARCODE:
    '/business_api/me/businesses/:businessId/warehouse/barcode',
  WAREHOUSE_BARCODE_GENERATOR:
    '/business_api/me/businesses/:businessId/warehouse/barcode_generator',
  WAREHOUSE_BRANDS: '/business_api/me/businesses/:businessId/warehouse/brands',
  WAREHOUSE_CATEGORIES:
    '/business_api/me/businesses/:businessId/warehouse/categories',
  WAREHOUSE_CATEGORY_TREE:
    '/business_api/me/businesses/:businessId/warehouse/category_tree',
  WAREHOUSE_CATEGORY:
    '/business_api/me/businesses/:businessId/warehouse/category',
  WAREHOUSE_COMMODITY_HISTORY:
    '/business_api/me/businesses/:businessId/warehouse/commodity/history',
  WAREHOUSE_PRODUCT_HISTORY:
    '/business_api/me/businesses/:businessId/commodity/:commodityId/warehouse/:warehouseId/history',
  WAREHOUSE_WAREHOUSES_DETAILS:
    '/business_api/me/businesses/:businessId/warehouse/warehouses/:warehouseId',
  WAREHOUSE_WAREHOUSES_DETAILS_COMMODITIES:
    '/business_api/me/businesses/:businessId/warehouse/warehouses/:warehouseId/commodities',
  WAREHOUSE_REPORTS:
    '/business_api/me/businesses/:businessId/business_statistics',
  WAREHOUSE_PHOTO: '/business_api/me/businesses/:businessId/warehouse/photo',
  WAREHOUSE_QUICK_ORDER:
    '/business_api/me/businesses/:businessId/warehouse/documents/quick_order/',

  /*! appointment */
  APPOINTMENTS: '/business_api/me/businesses/:businessId/appointments/',
  APPOINTMENT:
    '/business_api/me/businesses/:businessId/appointments/:appointmentType/:appointmentId',
  APPOINTMENTS_DRYRUN:
    '/business_api/me/businesses/:businessId/appointments/dry_run/',
  APPOINTMENT_UID:
    '/business_api/me/businesses/:businessId/appointments/:appointmentUid/',
  APPOINTMENT_UID_DRYRUN:
    '/business_api/me/businesses/:businessId/appointments/:appointmentUid/dry_run/',
  APPOINTMENT_ACTION:
    '/business_api/me/businesses/:businessId/appointments/:appointmentUid/action',
  APPOINTMENT_DRAG:
    '/business_api/me/businesses/:businessId/calendar/drag_subbooking/:bookingId/',
  APPOINTMENT_READY:
    '/business_api/me/businesses/:businessId/appointments/:appointmentUid/notify_ready/',
  APPOINTMENT_REDO:
    '/business_api/me/businesses/:businessId/appointments/:appointmentUid/book_again/',
  REPEATING:
    '/business_api/me/businesses/:businessId/repeating_bookings/:repeatingId/',
  RESERVATION: '/business_api/me/reservations/:reservationId/',
  RESERVATION_NEW: '/business_api/me/businesses/:businessId/reservations/',

  /*! images */
  IMAGES: '/business_api/me/businesses/:businessId/images/v2',
  IMAGE: '/business_api/me/businesses/:businessId/images/v2/:imageId/',
  IMAGE_COMMENTS:
    '/business_api/me/businesses/:businessId/images/v2/:imageId/comments',
  IMAGE_COMMENT:
    '/business_api/me/businesses/:businessId/images/v2/:imageId/comments/:commentId/',
  IMAGES_COVER: '/business_api/me/businesses/:businessId/images/v2/cover',
  IMAGE_SET_COVER:
    '/business_api/me/businesses/:businessId/images/v2/:imageId/set_cover',
  IMAGE_MOVE_AFTER:
    '/business_api/me/businesses/:businessId/images/v2/:imageId/move_after',
  IMAGE_MOVE_BEFORE:
    '/business_api/me/businesses/:businessId/images/v2/:imageId/move_before',

  /*! notifications */
  NOTIFICATIONS:
    '/business_api/me/businesses/:businessId/notification_list/:status/',

  /* VOUCHER */
  VOUCHERS: '/business_api/me/businesses/:businessId/vouchers/',
  VOUCHERS_ID: '/business_api/me/businesses/:businessId/vouchers/:voucherId',
  VOUCHERS_SIMPLE_REDEEM:
    '/business_api/me/businesses/:businessId/vouchers/simple_redeem/',
  VOUCHER_ORDERS: '/business_api/me/businesses/:businessId/voucher_orders/',
  VOUCHER_ORDERS_ID:
    '/business_api/me/businesses/:businessId/voucher_orders/:orderId',
  VOUCHER_ORDERS_ID_FINALIZE:
    '/business_api/me/businesses/:businessId/voucher_orders/:orderId/finalize',
  VOUCHER_TEMPLATES:
    '/business_api/me/businesses/:businessId/voucher_templates/',
  VOUCHER_SINGLE_TEMPLATE:
    '/business_api/me/businesses/:businessId/voucher_templates/:templateId',
  VOUCHER_ADDITIONAL_INFO:
    '/business_api/me/businesses/:businessId/voucher_additional_info/',
  VOUCHER_PURCHASE_METHODS_SETTINGS:
    '/business_api/me/businesses/:businessId/voucher_purchase_methods_settings/',
  VOUCHER_BACKGROUND_IMAGES:
    '/business_api/me/businesses/:businessId/voucher_background_images/',

  /* SCHEDULE */
  SHIFTS: '/business_api/me/businesses/:businessId/shifts',
  SHIFTS_OPENING_HOURS:
    '/business_api/me/businesses/:businessId/shifts/opening_hours',
  SHIFTS_WORKING_HOURS:
    '/business_api/me/businesses/:businessId/shifts/resources/:resourceId/working_hours',
  SHIFTS_RESOURCE:
    '/business_api/me/businesses/:businessId/shifts/resources/:resourceId',
  SHIFTS_COPY: '/business_api/me/businesses/:businessId/shifts/copy',
  SHIFTS_TIME_OFFS:
    '/business_api/me/businesses/:businessId/shifts/resources/time_offs',
  SHIFTS_TIME_OFF_EDIT:
    '/business_api/me/businesses/:businessId/shifts/resources/:resourceId/time_offs/:timeOffId',

  /* CONSENT FORM */
  CONSENT_FORMS: '/business_api/me/businesses/:businessId/consent_forms/',
  CONSENT_FORMS_ID:
    '/business_api/me/businesses/:businessId/consent_forms/:formId/',
  CONSENTS: '/business_api/me/businesses/:businessId/consents/',
  CONSENT_ID: '/business_api/me/businesses/:businessId/consents/:consentUuid/',
  CONSENT_ID_NOTIFY:
    '/business_api/me/businesses/:businessId/consents/:consentUuid/notify',
  CONSENT_ID_SIGN:
    '/business_api/me/businesses/:businessId/consents/:consentUuid/sign',
  CONSENT_ID_PDF:
    '/business_api/me/businesses/:businessId/consents/:consentUuid/pdf',
  CONSENT_ID_SEND_EMAIL:
    '/business_api/me/businesses/:businessId/consents/:consentUuid/send_email',

  /* MAPKIT */
  MAPKIT_TOKEN: '/other/apple_token/',
  CHECK_NEW_GEOLOCATION:
    '/business_api/me/businesses/:businessId/check_new_geolocation/',

  /* PROMOTIONS */
  TILE_STATUSES: '/business_api/me/businesses/:businessId/features_status/',
  TILE_STATUSES_PROMOTIONS:
    '/business_api/me/businesses/:businessId/features_status/service_promotions',
  TILE_STATUSES_PREMIUM_SERVICES:
    '/business_api/me/businesses/:businessId/premium_services/status/',
  PROMOTIONS: '/business_api/me/businesses/:businessId/promotions/',
  PROMOTION_SINGLE:
    '/business_api/me/businesses/:businessId/promotions/:promotionType',
  PREMIUM_SERVICES_SINGLE:
    '/business_api/me/businesses/:businessId/premium_services/:promotionType/',

  /* BUSINESS PROFILE */
  REVIEWS: '/business_api/me/businesses/:businessId/reviews/',
  REVIEWS_REPLY:
    '/business_api/me/businesses/:businessId/reviews/:reviewId/reply/',
  QR_CODE_BUSINESS_IMAGE:
    '/business_api/me/businesses/:businessId/qr_codes/business/',
  QR_CODE_BUSINESS_PDF:
    '/business_api/me/businesses/:businessId/qr_codes/business_package/',
  INSTAGRAM_ACCESS_TOKEN:
    '/business_api/me/businesses/:businessId/instagram/access_token/',
  INSTAGRAM_IMAGES: '/business_api/me/businesses/:businessId/instagram/images/',
  INSTAGRAM_PROFILE:
    '/business_api/me/businesses/:businessId/instagram/profile/',

  /* BOOST */
  BOOST_DASHBOARD: '/business_api/me/businesses/:businessId/boost/dashboard',
  BOOST_DETAILS: '/business_api/me/businesses/:businessId/boost/details',
  BOOST_COST: '/business_api/me/businesses/:businessId/boost/cost',
  BOOST_POLL: '/business_api/me/businesses/:businessId/poll',
  CLAIM_BOOST_CLIENT:
    '/business_api/me/businesses/:businessId/boost/claim_client',
  CLAIM_UPLOAD_URLS:
    '/business_api/me/businesses/:businessId/boost/claim_upload_urls',
  BOOST_SUSPEND: '/business_api/me/businesses/:businessId/boost/suspend',
  BOOST_ENABLE: '/business_api/me/businesses/:businessId/boost/enable',
  BOOST_CONDITIONS: '/business_api/me/businesses/:businessId/boost/conditions',
  BOOST_PREVIEW: '/business_api/me/businesses/:businessId/boost/preview/',
  BOOST_STATUS:
    '/business_api/me/businesses/:businessId/features_status/boost/',
  BOOST_BUSINESS_VERIFIED:
    '/business_api/me/businesses/:businessId/boost/verified',
  BOOST_RETRY_CHARGE:
    '/business_api/me/businesses/:businessId/billing/boost/overdue/retry_charge/',

  /* PROFILE COMPLETENESS */
  POST_OFFLINE_STEP:
    '/business_api/me/businesses/:businessId/profile_completeness/steps/finish/:stepInternalName/',
  GET_ALL_STEPS:
    '/business_api/me/businesses/:businessId/profile_completeness/tiers/',
  GET_ACTIVE_STEP:
    '/business_api/me/businesses/:businessId/profile_completeness/tiers/active/',

  /* IMPORT CUSTOMERS */
  IMPORT_FROM_XLSX: '/business_api/me/businesses/:businessId/customers/import',
  IMPORT_JSON: '/business_api/me/businesses/:businessId/customers/import/json/',
  LAST_IMPORT:
    '/business_api/me/businesses/:businessId/customers/import/last_import',

  /* MERGE CUSTOMERS */
  GET_DUPLICATES:
    '/business_api/me/businesses/:businessId/customers/duplicates/',
  CUSTOMERS_MANUAL_MERGE:
    '/business_api/me/businesses/:businessId/customers/manual_merge',
  CUSTOMERS_MANUAL_MERGE_DRY:
    '/business_api/me/businesses/:businessId/customers/manual_merge/dry_run',

  /* MESSAGE TEMPLATES */
  MESSAGE_TEMPLATE_GROUPS:
    '/business_api/me/businesses/:businessId/message_blast/',
  SINGLE_GROUP:
    '/business_api/me/businesses/:businessId/message_blast/group/:groupId/',
  SINGLE_MESSAGE_TEMPLATE:
    '/business_api/me/businesses/:businessId/message_blast/:templateId/',
  POST_TEMPLATE:
    '/business_api/me/businesses/:businessId/message_blast/:templateId/',
  TEMPLATE_IMAGES:
    '/business_api/me/businesses/:businessId/message_blast/images/',
  ONETIME_SEND:
    '/business_api/me/businesses/:businessId/message_blast/:templateId/one_time_send/',
  RECIPIENTS_COUNTER:
    '/business_api/me/businesses/:businessId/message_blast/counter/',
  ACTIVATE_MODAL:
    '/business_api/me/businesses/:businessId/message_blast/activate/',
  SMS_MANAGEMENT: '/business_api/me/businesses/:businessId/sms/management/',
  COST_ESTIMATION:
    '/business_api/me/businesses/:businessId/message_blast/cost_estimation/',
  BLAST_TEMPLATE_IMAGES:
    '/business_api/me/businesses/:businessId/message_blast/template_images/',
  BLAST_CUSTOM_IMAGE:
    '/business_api/me/businesses/:businessId/message_blast/image/',
  ACTIVATE_RECOMMENDED:
    '/business_api/me/businesses/:businessId/message_blast/activate_recommended',
  BLAST_PREVIEW:
    '/business_api/me/businesses/:businessId/message_blast/content/',
  BLAST_IMAGES_TREE:
    '/business_api/me/businesses/:businessId/message_blast/template_images/category_tree/',
  BLAST_ALL_IMAGES:
    '/business_api/me/businesses/:businessId/message_blast/template_images/all_images/',
  SCHEDULED_BLASTS:
    '/business_api/me/businesses/:businessId/message_blast/scheduled/',
  DELETE_SCHEDULED:
    '/business_api/me/businesses/:businessId/message_blast/scheduled/:templateId/',
  SEND_TEST:
    '/business_api/me/businesses/:businessId/message_blast/:templateId/one_time_send_test/',
  BLAST_STOP_CODE:
    '/business_api/me/businesses/:businessId/message_blast/stop_code/',

  /* SUBSCRIPTIONS */
  SUBSCRIPTIONS: '/business_api/me/businesses/:businessId/subscriptions',
  CLIENT_TOKEN:
    '/business_api/me/businesses/:businessId/subscriptions/braintree/client_token',
  INVOICE: '/business_api/me/businesses/:businessId/subscriptions/invoice',
  INVOICE_SEND:
    '/business_api/me/businesses/:businessId/subscriptions/invoice/send/:invoiceId',
  SUBSCRIPTION_PLANS:
    '/business_api/me/businesses/:businessId/subscriptions/listing',
  CAN_RETRY_DEPRECATED:
    '/business_api/me/businesses/:businessId/subscriptions/braintree/retry',
  CAN_RETRY:
    '/business_api/me/businesses/:businessId/subscriptions/braintree/retry/async',
  BRAINTREE_SUBSCRIPTION:
    '/business_api/me/businesses/:businessId/subscriptions/braintree/subscription/async',
  BRAINTREE_TASK_STATUS:
    '/business_api/me/businesses/:businessId/subscriptions/braintree/task_status/:taskId',
  SUBSCRIPTION_BUYER:
    '/business_api/me/businesses/:businessId/subscriptions/subscription_buyer',
  SUBSCRIPTION_BUYER_V2:
    '/business_api/me/businesses/:businessId/navision/invoice_details/',
  SUBSCRIPTION_LISTING:
    '/business_api/me/businesses/:businessId/subscription_listing',
  GOOGLE_SUBSCRIPTION_V4:
    '/business_api/me/businesses/:businessId/subscriptions/google_v4',
  GOOGLE_SUBSCRIPTION_RESTORE_V4:
    '/business_api/me/businesses/:businessId/subscriptions/google_v4/restore',
  APPLE_SUBSCRIPTION:
    '/business_api/me/businesses/:businessId/subscriptions/apple/validate',
  APPLE_SUBSCRIPTION_TASK_STATUS:
    '/business_api/me/businesses/:businessId/subscriptions/apple/task/:taskId',
  OFFLINE_SUBSCRIPTION_REQUEST:
    '/business_api/me/businesses/:businessId/subscriptions/offline/request',
  OFFLINE_MIGRATION_POPUP:
    '/business_api/me/businesses/:businessId/billing/settings/offline_migration/reminder/',

  BILLING_BRAINTREE_CLIENT_TOKEN:
    '/business_api/me/businesses/:businessId/billing/braintree/client_token/',

  BILLING_INFO:
    '/business_api/me/businesses/:businessId/billing/braintree/billing_info/async/',

  BILLING_SUBSCRIPTION_PURCHASE:
    '/business_api/me/businesses/:businessId/billing/subscription/purchase/',
  BILLING_SUBSCRIPTION:
    '/business_api/me/businesses/:businessId/billing/subscription/async/ ',
  BILLING_SUBSCRIPTION_TASK:
    '/business_api/me/businesses/:businessId/billing/task_status/:taskId/',
  BILLING_SUBSCRIPTION_DETAILS:
    '/business_api/me/businesses/:businessId/billing/subscription/details/',

  BILLING_OFFER:
    '/business_api/me/businesses/:businessId/billing/subscriptions/offer/',
  BILLING_GLOBAL_SETTINGS: '/routing/config/billing',
  BILLING_BUSINESS_SETTINGS:
    '/business_api/me/businesses/:businessId/billing/settings/',

  BILLING_PAYMENT_METHOD:
    '/business_api/me/businesses/:businessId/billing/payment_method/',
  BILLING_PAYMENT_METHODS:
    '/business_api/me/businesses/:businessId/billing/payment_methods/',
  BILLING_PAYMENT_METHOD_DELETE:
    '/business_api/me/businesses/:businessId/billing/payment_methods/:paymentMethodId/',
  BILLING_PAYMENT_SET_DEFAULT:
    '/business_api/me/businesses/:businessId/billing/payment_methods/:paymentMethodId/default/',
  BILLING_PAYMENT_METHOD_UPDATE:
    '/business_api/me/businesses/:businessId/billing/payment_methods/:paymentMethodId/',

  BILLING_TERMS: '/business_api/billing_terms',
  BILLING_NEXT_PAYMENT:
    '/business_api/me/businesses/:businessId/billing/next_payment',
  BILLING_OVERDUE_DETAILS:
    '/business_api/me/businesses/:businessId/billing/overdue_details',
  BILLING_RETRY_CHARGE:
    '/business_api/me/businesses/:businessId/billing/subscription/retry/async',
  BILLING_RETRY_CHARGE_TASK_STATUS:
    '/business_api/me/businesses/:businessId/billing/subscription/retry/async',
  BILLING_CHECK_ACCESS:
    '/business_api/me/businesses/:businessId/billing/grant_access_if_possible/',

  BILLING_NAVISION_SETTINGS:
    '/business_api/me/businesses/:businessId/navision/settings',
  BILLING_INVOICES:
    '/business_api/me/businesses/:businessId/navision/documents',
  BILLING_INVOICE_DETAILS:
    '/business_api/me/businesses/:businessId/navision/documents/:id',
  BILLING_OVERDUE_TOTAL:
    '/business_api/me/businesses/:businessId/billing/total_overdue',

  BILLING_SUBSCRIPTION_OVERDUE_DETAILS:
    '/business_api/me/businesses/:businessId/billing/subscription/overdue/details',

  BILLING_OVERDUE_BOOST:
    '/business_api/me/businesses/:businessId/billing/boost/overdue/appointments/',

  DISCOUNT_CODE:
    '/business_api/me/businesses/:businessId/billing/discount_code',
  BILLING_CYCLES: '/business_api/me/businesses/:businessId/billing/cycles',

  STRIPE_PAYMENT_METHOD:
    '/business_api/me/businesses/:businessId/billing/s/payment_method/async',

  /* PAYMENTS */
  TURN_ON_PBA:
    '/business_api/me/businesses/:businessId/market_pay/account/turn_on_pba',
  STRIPE_ACCOUNT_HOLDER_INFO:
    '/business_api/me/businesses/:businessId/payments/account_holder_settings/provider/stripe/',

  BOOKSY30_FEEDBACK:
    '/business_api/me/businesses/:businessId/booksy30_feedback/',

  /* NO-SHOW PROTECTION */
  NO_SHOW_PROTECTION:
    '/business_api/me/businesses/:businessId/pos/no_show_fee_service_protection/',

  /* INVITE CLIENTS */
  INVITE:
    '/business_api/me/businesses/:businessId/customers/:businessCustomerId/invite',
  INVITE_AGAIN: '/business_api/me/businesses/:businessId/invite_again/',
  QUICK_INVITE: '/business_api/me/businesses/:businessId/quick_invite/',

  /* HINTS */
  HINTS: '/business_api/me/businesses/:businessId/hints/',
  STORIES_DRAWER_HINTS: '/business_api/me/businesses/:businessId/stories/',

  /* REFERRALS */
  REFERRALS: '/business_api/me/businesses/:businessId/referral/',

  /* SEND EMAIL */
  SEND_EMAIL:
    '/business_api/me/businesses/:businessId/send_email/:templateName/',

  /* ONBOARDING */
  CREATE_ACCOUNT: '/business_api/account/',
  CREATE_BUSINESS: '/business_api/me/businesses/',
  ACTIVATE_BUSINESS: '/business_api/me/businesses/:businessId/activate/',
  B_LISTING: '/business_api/me/businesses/b_listing/',
  B_LISTING_CLAIM: '/business_api/me/businesses/b_listing/:bListingId/claim/',
  RESET_PASSWORD: '/business_api/account/password_reset/',
  PHONE_CHECK: '/utils/phone_check',
  PASSWORD_CHECK: '/utils/password_check',

  /* CELERY */
  CELERY_TASK_STATUS: '/celery/task/:taskId',

  /* online bookings */
  ONLINE_BOOKING_INTEGRATIONS:
    '/business_api/me/businesses/:businessId/online_booking_integrations',
  ONLINE_BOOKING_INTEGRATIONS_ACTIVATE:
    '/business_api/me/businesses/:businessId/online_booking_integrations/activate/',
  ONLINE_BOOKING_INTEGRATIONS_DEACTIVATE:
    '/business_api/me/businesses/:businessId/online_booking_integrations/deactivate/',

  /* facebook connectors and widgets */
  FACEBOOK_OAUTH: '/business_api/me/facebook/v3/dialog_oauth',
  FACEBOOK_API_BASE_URL: '/business_api/me/facebook/v3',

  /* PARTNER APPS */
  INSTALLED_PARTNER_APPS:
    '/business_api/me/businesses/:businessId/partner_apps/',
  INSTALLED_PARTNER_APP_DETAILS:
    '/business_api/me/businesses/:businessId/partner_apps/:clientId/',
  PARTNER_APPS_CATALOG:
    '/business_api/me/businesses/:businessId/partner_apps/catalog/',
  PARTNER_APP_DETAILS:
    '/business_api/me/businesses/:businessId/partner_apps/catalog/:clientId/',
  PARTNER_APP_SUSPEND:
    '/business_api/me/businesses/:businessId/partner_apps/:clientId/suspend/',

  /* REPORTS */
  NEW_STATS_DASHBOARD:
    '/business_api/me/stats/businesses/:businessId/dashboard',
  NEW_STATS_DASHBOARD_MAIN:
    '/business_api/me/stats/businesses/:businessId/dashboard/main',
  NEW_STATS_REPORT: '/business_api/me/stats/businesses/:businessId/report',
  NEW_STATS_DOWNLOAD:
    '/business_api/me/stats/businesses/:businessId/report/download',
  NEW_STATS_SECTION: '/business_api/me/stats/businesses/:businessId/section',

  /* SEQUENCING NUMBER */
  RECORDS_SETTINGS:
    '/business_api/me/sequencing_number/:businessId/records_settings',

  /* SOCIAL POSTS/DIGITAL FLYERS */
  DIGITAL_FLYER: '/business_api/me/businesses/:businessId/digital_flyer/',
  DIGITAL_FLYER_CATEGORY:
    '/business_api/me/businesses/:businessId/digital_flyer/:dfCategoryId',
  FLYER_DATA:
    '/business_api/me/businesses/:businessId/digital_flyer/flyer_data/',
  DIGITAL_FLYER_HASHTAGS:
    '/business_api/me/businesses/:businessId/digital_flyer/hashtags',
  DIGITAL_FLYER_SHARED:
    '/business_api/me/businesses/:businessId/digital_flyer/shared',
  DIGITAL_FLYER_RENDER:
    '/business_api/me/businesses/:businessId/digital_flyer/render',
  CLOUDFRONT_PROXY: '/cloudfront_proxy/:proxyUrl',

  /* HEALTH & SAFETY RULES */
  SAFETY_RULES: '/business_api/me/businesses/:businessId/safety_rules/',

  /* UMBRELLA VENUE */
  UMBRELLA_VENUE: '/business_api/me/businesses/:businessId/renting_venue/',
  UMBRELLA_VENUES:
    '/business_api/me/businesses/:businessId/renting_venue/contractors/',
  UMBRELLA_VENUE_COVER_PHOTO:
    '/business_api/me/businesses/:businessId/renting_venue/cover_photo/',
  UMBRELLA_VENUE_RESOLVER: '/business_api/search/venue_resolver/',

  /* AMENITIES */
  AMENITIES: '/business_api/me/businesses/:businessId/amenities',

  /* TOKEN */
  ANDROID_TOKEN:
    '/business_api/me/businesses/:businessId/notifications/android/:token/',
  IOS_TOKEN:
    '/business_api/me/businesses/:businessId/notifications/ios/:token/',

  /* INTERNAL NOTIFICATIONS */
  INTERNAL_NOTIFICATIONS:
    '/business_api/me/businesses/:businessId/notifications/',
  INTERNAL_EMAILS_CHECK:
    '/business_api/me/businesses/:businessId/notifications/email/:oldEmail/:newEmail/check/',
  INTERNAL_EMAIL_NOTIFICATION:
    '/business_api/me/businesses/:businessId/notifications/email/:email/',

  /* NAVISION */
  NAVISION_ENOVA_INVOICE_PDF:
    '/business_api/me/businesses/:businessId/navision/documents/:id',

  /* TURN-TRACKER */
  TURN_TRACKER_STAFF:
    '/business_api/me/businesses/:businessId/turntracker/staff/',
  TURN_TRACKER_STAFF_AVAILABILITY:
    '/business_api/me/businesses/:businessId/turntracker/staff/availability/',
  TURN_TRACKER_STAFFER:
    '/business_api/me/businesses/:businessId/turntracker/staff/:stafferId',
  TURN_TRACKER_STAFF_ORDER:
    '/business_api/me/businesses/:businessId/turntracker/staff_order/',
  TURN_TRACKER_SETTINGS:
    '/business_api/me/businesses/:businessId/turntracker/settings/',
  TURN_TRACKER_GRID:
    '/business_api/me/businesses/:businessId/turntracker/grid/',
  TURN_TRACKER_PENTILE:
    '/business_api/me/businesses/:businessId/turntracker/pentile/',
  TURN_TRACKER_FINISH_TURN:
    '/business_api/me/businesses/:businessId/turntracker/finished_turns/',
  TURN_TRACKER_FINISHED_TURN_ID:
    '/business_api/me/businesses/:businessId/turntracker/finished_turns/:finishTurnId/',

  /* LOYALTY PROGRAM - API MICROSERVICE */
  LOYALTY_CARDS: '/loyalty-card-service/v1/loyalty-cards/:country/:businessId',
  LOYALTY_CARDS_ID:
    '/loyalty-card-service/v1/loyalty-cards/:country/:businessId/:cardId',
  LOYALTY_CARDS_ID_EARN_REWARD:
    '/loyalty-card-service/v1/loyalty-cards/:country/:businessId/:cardId/earn-reward',
  LOYALTY_HISTORY: '/loyalty-card-service/v1/history/:country/:businessId',
  LOYALTY_CARD_PROGRAMS_ID:
    '/loyalty-card-service/v1/loyalty-card-programs/:country/:businessId/:cardProgramId',
  LOYALTY_CARD_PROGRAMS_CURRENT:
    '/loyalty-card-service/v1/loyalty-card-programs/:country/:businessId/current',
  LOYALTY_CARD_PROGRAMS_STAMP:
    '/loyalty-card-service/v1/loyalty-card-programs/:country/:businessId/stamp',
  LOYALTY_CARDS_CARD_IMAGES:
    '/loyalty-card-service/v1/card-images/:country/:businessId',
  LOYALTY_CARDS_CARD_IMAGES_ID:
    '/loyalty-card-service/v1/card-images/:country/:businessId/:imageId',

  /* SALESFORCE IN-APP CHAT */
  SALESFORCE_IN_CHAT_APP_JWT: '/core/v2/salesforce_in_app_chat_jwt/',

  /* FRENCH CERTIFICATION */
  FISCAL_RECEIPTS:
    '/business_api/me/businesses/:businessId/nf525/fiscal_receipts',
  FISCAL_RECEIPTS_ID:
    '/business_api/me/businesses/:businessId/nf525/fiscal_receipts/:fiscalReceiptId',
  FISCAL_RECEIPT_CANCEL:
    '/business_api/me/businesses/:businessId/nf525/fiscal_receipts/:fiscalReceiptId/cancel',
  FISCAL_RECEIPT_PRINT:
    '/business_api/me/businesses/:businessId/nf525/fiscal_receipts/:fiscalReceiptId/print',
  FISCAL_RECEIPT_SEND:
    '/business_api/me/businesses/:businessId/nf525/fiscal_receipts/:fiscalReceiptId/send',
  FISCAL_ARCHIVES:
    '/business_api/me/businesses/:businessId/nf525/fiscal_archives',
  FISCAL_ARCHIVE_DOWNLOAD:
    '/business_api/me/businesses/:businessId/nf525/fiscal_archives/:fiscalArchiveId/url',
  FISCAL_PAYMENTS_SUMMARY:
    '/business_api/me/businesses/:businessId/nf525/payments_summary',
  CERTIFICATION_SETTINGS:
    '/business_api/me/businesses/:businessId/nf525/settings',

  /* POLLS */
  POLLS_LIST: '/business_api/me/businesses/:businessId/v2/polls_list/',
  POLL: '/business_api/me/businesses/:businessId/v2/poll/',

  /* HINT AND WALKTHROUGHS - API MICROSERVICE */
  HINT_AND_WALKTHROUGH_HINTS_LIST:
    '/hint-and-walkthrough/v2/:country/:userId/hints/',
  HINT_AND_WALKTHROUGH_UPDATE_HINT_STATUS:
    '/hint-and-walkthrough/v2/:country/:userId/hints/:internalName/',

  /* UNIVERSAL BANNERS */
  CALENDAR_BANNERS: '/business_api/me/businesses/:businessId/calendar/banners/',
  SETTINGS_BANNERS: '/business_api/me/businesses/:businessId/settings/banners/',

  CALENDAR_BANNERS_REDIRECTION:
    '/business_api/me/businesses/:businessId/calendar/banners/redirection',

  /* TTP SPLASH IN SETTINGS */
  PROFILE_SETTINGS_SPLASH:
    '/business_api/me/businesses/:businessId/profile_settings_splash/',
  TTP_REMINDER_SPLASH:
    '/business_api/me/businesses/:businessId/ttp_reminder_splash/',

  /* CALENDAR SPLASHES */
  CALENDAR_SPLASH: '/business_api/me/businesses/:businessId/calendar/splash/',

  ITERABLE_JWT: '/business_api/me/iterable/',

  /* 2FA */
  OTP_RESEND: '/otp/resend',
}

// when adding languages here, add them to `availableLocales` in start.html file as well
export const LANGUAGES = [
  {
    code: 'en',
    name: 'English (United States)',
  },
  {
    code: 'en-gb',
    name: 'English (Great Britain)',
  },
  {
    code: 'pl',
    name: 'Polski',
  },
  {
    code: 'es-es',
    name: 'Español (España)',
  },
  {
    code: 'es',
    name: 'Español',
  },
  {
    code: 'pt',
    name: 'Português (Brasil)',
  },
  {
    code: 'fr',
    name: 'Français',
  },
  {
    code: 'vi',
    name: 'Tiếng Việt',
  },
  {
    code: 'uk',
    name: 'Українська',
  },
  {
    code: 'zh',
    name: '简体中文',
  },
  {
    code: 'de',
    name: 'Deutsch',
  },
]

/* sidebar visibility class */
export const HTML_SIDEBAR_CLASS = 'view-with-sidebar'

/* technical formats to use with moment js */
export const FORMAT = 'YYYY-MM-DDTHH:mm'
export const DATE_FORMAT = 'YYYY-MM-DD'
export const TIME_FORMAT = 'HH:mm'

/* view modes */
export const VIEW_DAY = 'day'
export const VIEW_WEEK = 'week'
export const VIEW_MONTH = 'month'
export const VIEW_AGENDA = 'agenda'

/* resource types */
export const RESOURCE_TYPE_APPLIANCE = 'R'
export const RESOURCE_TYPE_STAFFER = 'S'

/* booking types */
export const BOOKING_TYPE_BUSINESS = 'B'
export const BOOKING_TYPE_CUSTOMER = 'C'
export const BOOKING_TYPE_RESERVATION = 'R'
export const BOOKING_TYPE_APPOINTMENT = 'A'
export const BOOKING_TYPE_TIMEOFF = 'T'
export const BOOKING_TYPE_BREAK = 'NB'
export const BOOKING_TYPE_NOT_WORKING = 'NW'

/* booking statuses map */
export const APPOINTMENT_STATUSES_MAP = {
  ACCEPTED: 'A',
  CANCELED: 'C',
  DECLINED: 'D',
  FINISHED: 'F',
  MODIFIED: 'M',
  NOSHOW: 'N',
  PREPAYMENT_AWAITING: 'O',
  PROPOSED: 'P',
  UNCONFIRMED: 'W',
  REJECTED: 'R',
  UNVERIFIED: 'V',
}

export const APPOINTMENT_STATUSES = {
  [APPOINTMENT_STATUSES_MAP.ACCEPTED]: {
    class: 'statusGreen',
    label: 'appointment-status.confirmed',
  },
  [APPOINTMENT_STATUSES_MAP.CANCELED]: {
    class: 'statusGray',
    label: 'appointment-status.canceled',
  },
  [APPOINTMENT_STATUSES_MAP.DECLINED]: {
    class: 'statusRed',
    label: 'appointment-status.declined',
  },
  [APPOINTMENT_STATUSES_MAP.FINISHED]: {
    class: 'statusGreen',
    label: 'appointment-status.finished',
  },
  [APPOINTMENT_STATUSES_MAP.MODIFIED]: {
    class: 'statusBlue',
    label: 'appointment-status.modified',
  },
  [APPOINTMENT_STATUSES_MAP.NOSHOW]: {
    class: 'statusRed',
    label: 'appointment-status.no-show',
  },
  [APPOINTMENT_STATUSES_MAP.PROPOSED]: {
    class: 'statusOrange',
    label: 'appointment-status.client-must-confirm',
  },
  [APPOINTMENT_STATUSES_MAP.UNCONFIRMED]: {
    class: 'statusOrange',
    label: 'appointment-status.you-must-confirm',
  },
  [APPOINTMENT_STATUSES_MAP.REJECTED]: {
    class: 'statusRed',
    label: 'appointment-status.rejected',
  },
  [APPOINTMENT_STATUSES_MAP.UNVERIFIED]: {
    class: 'statusOrange',
    label: 'appointment-status.unverified',
  },
  [APPOINTMENT_STATUSES_MAP.PREPAYMENT_AWAITING]: {
    class: 'statusOrange',
    label: 'appointment-status.prepayment-awaiting',
  },
}

export const APPOINTMENT_MODES = {
  NEW: 'new',
  EDIT: 'edit',
}

export const APPOINTMENT_TYPE = {
  SINGLE: 'single',
  MULTI: 'multi',
}

export const APPOINTMENT_EXTERNAL_SOURCE = {
  GOOGLE: 'google',
  YELP: 'yelp',
  GROUPON: 'groupon',
  INSTAGRAM: 'instagram',
  INSTAGRAM_STAFFER: 'instagram_staffer',
  FACEBOOK: 'facebook',
}

export const APPOINTMENT_TABS = {
  APPOINTMENT: 'appointment',
  NOTES_AND_INFO: 'notes-and-info',
  TRAVELING: 'traveling',
  ONLINE_SERVEICES: 'online-services',
}

export const APPOINTMENT_TYPE_SHORT = Object.keys(APPOINTMENT_TYPE).reduce(
  (obj, key) => {
    obj[APPOINTMENT_TYPE[key][0]] = APPOINTMENT_TYPE[key]
    return obj
  },
  {}
)

export const CUSTOMER_MODES = {
  WALKIN: 'walk-in',
  CARD: 'customer-card',
  MANUAL: 'manual',
}

export const PAYMENT_STATUSES = {
  call_for_payment: {
    class: 'yellow',
    label: 'payments-status.call-for-payment',
  },
  in_progress: {
    class: 'yellow',
    label: 'payments-status.in-progress',
  },
  call_for_deposit: {
    class: 'yellow',
    label: 'payments-status.call-for-deposit',
  },
  prepayment: {
    class: 'green',
    label: 'payments-status.paid',
  },
  partial_settlement: {
    class: 'green',
    label: 'payments-status.paid',
  },
  pending: {
    class: 'yellow',
    label: 'payments-status.pending',
  },
  authorized: {
    class: 'yellow',
    label: 'payments-status.authorized',
  },
  charged: {
    class: 'green',
    label: 'payments-status.paid',
  },
  awaiting: {
    class: 'yellow',
    label: 'payments-status.awaiting',
  },
  success: {
    class: 'green',
    label: 'payments-status.paid',
  },
  failed: {
    class: 'red',
    label: 'payments-status.failed',
  },
  canceled: {
    class: 'grey',
    label: 'payments-status.canceled',
  },
  archived: {
    class: 'grey',
    label: 'payments-status.archived',
  },
  park_sale: {
    class: 'yellow',
    label: 'payments-status.park-sale',
  },
  sent_for_refund: {
    class: 'yellow',
    label: 'payments-status.sent-for-refund',
  },
  refunded: {
    class: 'grey',
    label: 'payments-status.refunded',
  },
  chargeback: {
    class: 'red',
    label: 'payments-status.chargeback',
  },
  second_chargeback: {
    class: 'statusGreen',
    label: 'payments-status.second-chargeback',
  },
  booksy_pay: {
    class: 'green',
    label: 'payments-status.paid',
  },
  booksy_gift_card: {
    class: 'statusOrange',
    label: 'payments-status.booksy-gift-card',
  },
}

export const PAYMENT_SHORT_STATUSES = {
  archived: {
    class: 'statusGray',
    label: 'payments-status.archived',
  },
  authorized: {
    class: 'statusOrange',
    label: 'payments-status.authorized',
  },
  awaiting: {
    class: 'statusOrange',
    label: 'payments-status.awaiting',
  },
  call_for_deposit: {
    class: 'statusOrange',
    label: 'payments-status.call-for-deposit',
  },
  call_for_payment: {
    class: 'statusOrange',
    label: 'payments-status.call-for-payment',
  },
  canceled: {
    class: 'statusGray',
    label: 'payments-status.canceled',
  },
  charged: {
    class: 'statusGreen',
    label: 'payments-status.paid',
  },
  chargeback: {
    class: 'statusRed',
    label: 'payments-status.chargeback',
  },
  failed: {
    class: 'statusRed',
    label: 'payments-status.failed',
  },
  in_progress: {
    class: 'statusOrange',
    label: 'payments-status.in-progress',
  },
  park_sale: {
    class: 'statusOrange',
    label: 'payments-status.park-sale',
  },
  pending: {
    class: 'statusOrange',
    label: 'payments-status.pending',
  },
  partial_settlement: {
    class: 'statusGreen',
    label: 'payments-status.paid',
  },
  prepayment: {
    class: 'statusGreen',
    label: 'payments-status.paid',
  },
  refunded: {
    class: 'statusGray',
    label: 'payments-status.refunded',
  },
  second_chargeback: {
    class: 'statusGreen',
    label: 'payments-status.second-chargeback',
  },
  sent_for_refund: {
    class: 'statusOrange',
    label: 'payments-status.sent-for-refund',
  },
  success: {
    class: 'statusGreen',
    label: 'payments-status.paid',
  },
  chargeback_reversed: {
    class: 'statusGreen',
    label: 'payments-status.chargeback-reversed',
  },
  booksy_pay: {
    class: 'statusGreen',
    label: 'payments-status.paid',
  },
  booksy_gift_card: {
    class: 'statusOrange',
    label: 'payments-status.booksy-gift-card',
  },
}

export const PRICE_TYPES = {
  FIXED: 'X',
  VARIES: 'V',
  DONT_SHOW: 'D',
  FREE: 'F',
  STARTS_AT: 'S',
}

export const PRICE_TYPES_DATA = {
  [PRICE_TYPES.FIXED]: {
    label: __t('fixed'),
  },
  [PRICE_TYPES.VARIES]: {
    label: __t('varies'),
  },
  [PRICE_TYPES.DONT_SHOW]: {
    label: __t('do_not_show'),
  },
  [PRICE_TYPES.FREE]: {
    label: __t('free'),
  },
  [PRICE_TYPES.STARTS_AT]: {
    label: __t('starts_at'),
  },
}

export const TRAVEL_FEE_PRICE_TYPES_DATA = {
  [PRICE_TYPES.FIXED]: {
    label: __t('fixed'),
  },
  [PRICE_TYPES.VARIES]: {
    label: __t('varies'),
  },
  [PRICE_TYPES.FREE]: {
    label: __t('free'),
  },
  [PRICE_TYPES.STARTS_AT]: {
    label: __t('starts_at'),
  },
}

export const RECEIPT_STATUSES = {
  AUTHORIZED: 'authorized',
  SUCCESS: 'success',
  FAILED: 'failed',
  PREPAYMENT: 'prepayment',
  CALL_FOR_DEPOSIT: 'call_for_deposit',
  REFUNDED: 'refunded',
  BOOKSY_PAY: 'booksy_pay',
  BOOKSY_GIFT_CARD: 'booksy_gift_card',
}

export const MODALS = {
  APPOINTMENT_CONFIRM_UPDATE: 'APPOINTMENT_CONFIRM_UPDATE',
  APPOINTMENT_UPDATE_FUTURE: 'APPOINTMENT_UPDATE_FUTURE',
  APPOINTMENT_OVERBOOKING: 'APPOINTMENT_OVERBOOKING',
  APPOINTMENT_CANCEL: 'APPOINTMENT_CANCEL',
  APPOINTMENT_NO_SHOW: 'APPOINTMENT_NO_SHOW',
  APPOINTMENT_DISCARD: 'APPOINTMENT_DISCARD',
  APPOINTMENT_CHANGE_RESOURCE: 'APPOINTMENT_CHANGE_RESOURCE',
  APPOINTMENT_COMBO_EDIT: 'APPOINTMENT_COMBO_EDIT',
  CUSTOMER_RECEIPT: 'CUSTOMER_RECEIPT',
  CUSTOMER_AGREEMENT: 'CUSTOMER_AGREEMENT',
  CUSTOMER_TRUSTED_CLIENT: 'CUSTOMER_TRUSTED_CLIENT',
  CUSTOMER_DELETE: 'CUSTOMER_DELETE',
  CUSTOMER_NEW: 'CUSTOMER_NEW',
  FAMILY_FRIEND_NEW: 'FAMILY_FRIEND_NEW',
  ADD_CATEGORY_MODAL: 'ADD_CATEGORY_MODAL',
  RECEIPT: 'RECEIPT',
  ACCOUNT_SETTINGS: 'ACCOUNT_SETTINGS',
  APPOINTMENT_RELEASE_DEPOSIT: 'APPOINTMENT_RELEASE_DEPOSIT',
  APPOINTMENT_BOOKING_MODE: 'APPOINTMENT_BOOKING_MODE',
  OTP_MODAL: 'OTP_MODAL',
}

export const ACCOUNTING_SETTINGS = {
  ng: Dollar('₦'),
  za: Euro('R', false, false),

  in: Dollar('₹', true),
  jp: Dollar('¥', false, false, 0),
  my: Dollar('RM'),
  ph: Dollar('₱'),
  sg: Dollar('$'),

  au: Dollar('$'),

  dk: Euro('kr', true, false),
  fi: Euro('€'),
  fr: Euro('€'),
  de: Euro('€'),
  ie: Dollar('€'),
  it: Euro('€', true, false),
  nl: Euro('€', true, false),
  pl: Euro('zł'),
  pt: Euro('€'),
  ru: Euro('₽'),
  es: Euro('€'),
  se: Euro('kr', true, false),
  gb: Dollar('£'),

  us: Dollar('$'),
  mx: Dollar('$', true),
  ca: Dollar('$'),
  hk: Dollar('HK$'),

  ar: Dollar('$', true),
  br: Dollar('R$', true),
  ve: Dollar('Bs.', true),
  co: Dollar('$', true),
  cl: Dollar('$', false, false, 0),
}

/*! defaults for image uploader & cropper */
export const IMAGE_OPTIONS = {
  type: 'image/jpeg',
  quality: 0.8,
  maxWidth: 2500,
  maxHeight: 2500,
}

export const IS_CAPTURE_SUPPORTED = (() => {
  const i = document.createElement('input')
  i.setAttribute('capture', 'true')
  return !!i.capture
})()

export const BUSINESS_STATUSES = {
  SETUP: 'S',
  TRIAL: 'T',
  TRIAL_END: 'E', // deprecated business status, new statuses: ticket 66549
  TRIAL_BLOCKED: 'F',
  PAID_ACTIVE: 'P',
  ACTIVE_OVERDUE: 'O',
  BLOCKED_OVERDUE: 'L',
  CHURNED: 'R', // New status
  SUSPENDED: 'H', // New status
  BUSINESS_INVALID: 'B', // prev name: BLOCKED
  VENUE: 'V',
  B_LISTING: 'C',
  DEMO: 'D',
  DEMO_TEMPLATE: 'M',
}

export const ACCESS_LEVELS = {
  OWNER: 'owner',
  MANAGER: 'manager',
  RECEPTION: 'reception',
  ADVANCED: 'advanced',
  STAFF: 'staff',
}

export const ACCESS_LEVELS_ORDERED = [
  ACCESS_LEVELS.OWNER,
  ACCESS_LEVELS.MANAGER,
  ACCESS_LEVELS.RECEPTION,
  ACCESS_LEVELS.ADVANCED,
  ACCESS_LEVELS.STAFF,
]

export const SERVICE_CATEGORY_SHOW_OPTIONS = {
  SHOW_ALL: {
    value: -1,
    label: __t('components.service.labels.categories.show-all'),
  },
  SHOW_FIRST_5: {
    value: 5,
    label: __t('components.service.labels.categories.show-first'),
  },
  SHOW_NONE: {
    value: 0,
    label: __t('components.service.labels.categories.show-none'),
  },
}

export const SERVICE_PADDING_TIME_RULES = {
  NONE: {
    value: '',
    label: __t('components.service.labels.rules.none'),
  },
  AFTER: {
    value: 'A',
    label: __t('components.service.labels.rules.after'),
  },
  BEFORE: {
    value: 'B',
    label: __t('components.service.labels.rules.before'),
  },
  BOTH: {
    value: 'C',
    label: __t('components.service.labels.rules.both'),
  },
}

export const SERVICE_VARIANT_TYPES = {
  [PRICE_TYPES.FREE]: 'components.customer.free',
  [PRICE_TYPES.VARIES]: 'components.customer.varies',
  [PRICE_TYPES.DONT_SHOW]: '--',
}

export const VOUCHER_TYPES = {
  GIFT_CARD: 'G',
  MEMBERSHIP: 'M',
  PACKAGE: 'P',
}

export const VOUCHER_VALID_TILL_TYPE = {
  UNLIMITED: 'U',
  DAYS_30: 'days_30',
  DAYS_90: 'days_90',
  MONTHS_6: 'months_6',
  YEAR_1: 'year_1',
  END_OF_MONTH: 'M',
  END_OF_YEAR: 'Y',
}

export const VOUCHER_EXPIRATION_OPTIONS = [
  {
    label: __t('components.gifts.consts.expiration-options.never'),
    value: VOUCHER_VALID_TILL_TYPE.UNLIMITED,
  },
  {
    label: __t('components.gifts.consts.expiration-options.30-days'),
    value: VOUCHER_VALID_TILL_TYPE.DAYS_30,
  },
  {
    label: __t('components.gifts.consts.expiration-options.90-days'),
    value: VOUCHER_VALID_TILL_TYPE.DAYS_90,
  },
  {
    label: __t('components.gifts.consts.expiration-options.6-months'),
    value: VOUCHER_VALID_TILL_TYPE.MONTHS_6,
  },
  {
    label: __t('components.gifts.consts.expiration-options.1-year'),
    value: VOUCHER_VALID_TILL_TYPE.YEAR_1,
  },
  {
    label: __t('components.gifts.consts.expiration-options.till-end-of-month'),
    value: VOUCHER_VALID_TILL_TYPE.END_OF_MONTH,
  },
  {
    label: __t('components.gifts.consts.expiration-options.till-end-of-year'),
    value: VOUCHER_VALID_TILL_TYPE.END_OF_YEAR,
  },
]

export const VOUCHER_VALID_TILL_PERIOD = {
  [VOUCHER_VALID_TILL_TYPE.DAYS_30]: { count: 30, unit: 'days' },
  [VOUCHER_VALID_TILL_TYPE.DAYS_90]: { count: 90, unit: 'days' },
  [VOUCHER_VALID_TILL_TYPE.MONTHS_6]: { count: 6, unit: 'months' },
  [VOUCHER_VALID_TILL_TYPE.YEAR_1]: { count: 1, unit: 'years' },
  [VOUCHER_VALID_TILL_TYPE.END_OF_MONTH]: 'month',
  [VOUCHER_VALID_TILL_TYPE.END_OF_YEAR]: 'year',
  // [VALID_TILL_TYPE.UNLIMITED]: ''
}

export const PAYMENT_TYPES_ICONS_MAP = {
  other: otherIcon,
  giftcard: giftcardIcon,
  egift_card: giftcardIcon,
  package: packageIcon,
  membership: membershipIcon,
  split: splitIcon,
  pay_by_app: mobilepaymentIcon,
  'card': mobilepaymentIcon,
  cash: cashIcon,
  credit_card: creditcardIcon,
  prepayment: mobilepaymentIcon,
  paypal: paypalIcon,
  american_express: creditcardIcon,
  check: checkIcon,
  square: squareIcon,
  stripe_terminal: stripeTerminalIcon,
  'terminal': stripeTerminalIcon,
  tap_to_pay: tapToPayIcon,
  booksy_pay: mobilepaymentIcon,
  booksy_gift_card: booksyGiftCardIcon,
  keyed_in_payment: keyedInPaymentIcon,
}

export const STRIPE_PAYMENT_METHODS = {
  CARD: 'card',
}

export const BUSINESS_STATUSES_MAP = {
  S: 'Configuration',
  T: 'Trial',
  F: 'Trial end blocked',
  P: 'Paid',
  O: 'Payment overdue active',
  L: 'Payment overdue blocked',
  H: 'Suspended',
  R: 'Churned',
  B: 'Business Invalid',
  V: 'Renting Venue',
  C: 'B Listing',
  D: 'Demo',
  M: 'Demo template',
}

export const DEV_CONSOLE_TYPE_TEXT = Symbol('text')
export const DEV_CONSOLE_TYPE_ERROR = Symbol('error')
export const DEV_CONSOLE_TYPE_REQUEST = Symbol('request')
export const DEV_CONSOLE_TYPE_ROUTE_CHANGE = Symbol('route-change')

export const DOCUMENT_TYPES = {
  STOCK_ISSUE_CONFIRMATION: 'WZ',
  INTERNAL_EXPENDITURE: 'RW',
  INTER_STOCK_TRANSFER: 'MM',
  INTERNAL_RECEPTION: 'PM',
  SUPPLY: 'PZ',
  ORDER: 'ORD',
  STOCKTAKING: 'INW',
}

export const DOCUMENT_STATUSES = {
  invalid: 'invalid',
  new: 'new',
  existing: 'existing',
}

export const DEFAULT_DOCUMENT_ROW = {
  _commodity: {},
  commodity: undefined,
  commodity_name: '',
  gross_price: 0,
  is_full_package_expenditure: true,
  is_full_package_inventoried: true,
  net_price: 0,
  quantity: '',
  quantity_inventoried: '',
  manually_assigned_number: '',
  tax: 0,
  unit: {
    label: '-',
  },
}

export const STATUS_COLORS = {
  STATUS_GREEN: '#00c479',
  STATUS_GREY: '#c0c0c0',
}

export const SCROLLABLE_OFFSETS = {
  SCROLLABLE_OFFSET_0: 0,
  SCROLLABLE_OFFSET_100: 100,
  SCROLLABLE_OFFSET_200: 200,
  SCROLLABLE_OFFSET_300: 300,
  SCROLLABLE_OFFSET_415: 415,
}

export const SCOPE_TYPES = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
  BETWEEN: 'between',
  LAST: 'last',
}

export const BILLING_PAYMENT_PROCESSORS = {
  STRIPE: 'S',
  BRAINTREE: 'B',
}

export const PAYMENT_SOURCES = {
  UNKNOWN: 'U',
  OFFLINE: 'O',
  ITUNES: 'I',
  PLAY: 'P',
  BOOKSY_BILLING: 'R',
}

export const PAYMENT_SOURCES_DISPLAY = {
  [PAYMENT_SOURCES.UNKNOWN]: __t('Unknown'),
  [PAYMENT_SOURCES.OFFLINE]: __t('Offline'),
  [PAYMENT_SOURCES.ITUNES]: __t('Apple iTunes'),
  [PAYMENT_SOURCES.PLAY]: __t('Google Play'),
  [PAYMENT_SOURCES.BOOKSY_BILLING]: __t('Booksy Billing'),
}

export const BILLING_METHODS = {
  GOOGLE_PLAY: 'GOOGLE_PLAY',
  ITUNES: 'ITUNES',
  BOOKSY_BILLING: 'BOOKSY_BILLING', // "new" billing
  UNAVAILABLE: 'UNAVAILABLE',
  OFFLINE: 'OFFLINE',
  TABLET: 'TABLET',
}

export const PORTFOLIO_ASIDE_OFFSET = 320

export const BOOKING_MODES = {
  AUTOMATIC: 'A',
  SEMIAUTOMATIC: 'S',
  MANUAL: 'M',
}

export const FACEBOOK_APP_ID = '523698674383043'
export const DEV_FACEBOOK_APP_ID = '286549602876576'

export const CALENDAR_VIEW_MODE = Object.freeze({
  AUTO: 'Auto',
  IMMEDIATE: 'Immediate',
  OPTIMIZED: 'Optimized',
})

export const EVENTS = {
  /* common modal */
  MODAL_OPEN: 'common-modal.open',
  MODAL_CLOSE: 'common-modal.close',
  MODAL_CANCEL: 'common-modal.cancel',
  // we need it here, because useEmitter validate event name from EVENTS const
  ...MODALS,

  // toast
  TOAST_ERROR: 'toast.error', // show toast error

  NOTIFICATIONS_DRAWER_OPEN: 'notifications.drawer.open',
  NOTIFICATIONS_DRAWER_CLOSE: 'notifications.drawer.close',

  CALENDAR_AGENDA_OPEN: 'calendar.agenda.open',
  CALENDAR_FEEDBACK_OPEN: 'calendar.feedback.open',
  CALENDAR_REFRESH: 'calendar.refresh',
  CALENDAR_DRAWERS_CLOSE: 'calendar.drawers.close',
  CALENDAR_FIRESTORE_DATA_RECEIVED: 'calendar.firestore.data.received',
  CALENDAR_FIRST_CUSTOMER_BOOKING_MODAL_OPEN:
    'calendar.first-customer-booking-modal.open',
  CALENDAR_SHOW_FIRST_BOOKING_CONGRATS_MODAL:
    'calendar.show-first-booking-congrats-modal',

  WEB_VIEW_RESUME: 'WebViewResume',
  WEB_VIEW_PUSH_OPEN: 'webview.push-open',

  HINTS_TOUR_START: 'hints.tour-start',
  HINTS_TOUR_STOP: 'hints.tour-stop',

  HINTS_MODAL_OPEN: 'hints.modal-open',

  CATEGORY_DELETED: 'category.deleted',

  ...APPOINTMENT_EVENTS,

  /* analytics */
  ANALYTICS_DISPATCH: 'analytics.dispatch',
  INIT: 'Init',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  PAGE_VIEW: 'PageView',
  CLICK: 'Click',
  /* analytics */

  BOOST_CLICKED_ON_MARKETING_LIST: 'BoostClickedOnMarketingList',
  BUSINESS_CATEGORIES_UPDATED: 'BusinessCategoriesUpdated',
  BUSINESS_INFO_UPDATED: 'BusinessInfoUpdated',
  BUSINESS_SWITCHED: 'BusinessSwitched',
  ENABLE_MOBILE_PAYMENTS_SCREEN_OPENED: 'EnableMobilePaymentsScreenOpened',
  FAST_PAYOUTS_ACTION: 'FastPayoutsAction',
  MOBILE_PAYMENTS_GET_STARTED_SELECTED: 'MobilePaymentsGetStartedSelected',
  ACCESS_TOKEN_ERROR: 'AccessTokenError',
  PORTFOLIO_PHOTOS_ADDED: 'PortfolioPhotosAdded',

  BUSINESS_USE_APP_POPUP_SHOWED: 'BusinessUseAppPopupShowed',
  BUSINESS_USE_APP_POPUP_OPEN_CLICKED: 'BusinessUseAppPopupOpenClicked',

  INVENTORY_PRODUCT_ADD_BARCODE_CLICKED: 'InventoryProductAddBarcodeClicked',
  INVENTORY_PRODUCT_EDIT_BARCODE_CLICKED: 'InventoryProductEditBarcodeClicked',

  INVITE_ALL_CLICKED: 'InviteAllClicked',
  INVITE_PROCESS_STARTED: 'InviteProcessStarted',
  INVITE_PROCESSING_STARTED: 'InviteProcessingStarted',
  INVITE_PROCESS_COMPLETED: 'InviteProcessCompleted',
  ACCESS_TO_ADDRESS_BOOK_GRANTED_DENIED: 'AccessToAddressBookGrantedDenied',

  // sale
  TRANSACTION_LIST_SHOWN: 'TransactionListShown',
  // addons
  AD_MAINSCREEN_ACTION: 'AdMainscreenAction',

  MESSAGE_BLAST_SENT: 'MessageBlastSent',
  MESSAGE_BLAST_ENTERED: 'MessageBlastEntered',

  // check version change
  CHECK_VERSION_CHANGE: 'CheckVersionChange',

  WEB_VIEW_CLEAR_CACHE: 'WebViewClearCache',

  // onboarding
  ONBOARDING_REFERRAL_VIEW_OPENED: 'OnboardingReferralViewOpened',
  ONBOARDING_NEW_EMAIL_ENTERED: 'OnboardingNewEmailEntered',
  ONBOARDING_COUNTRY_SELECTION: 'OnboardingCountrySelection',
  ONBOARDING_AGREEMENTS_UPDATED: 'OnboardingAgreementsUpdated',
  ONBOARDING_BUSINESS_NAME_SET: 'OnboardingBusinessNameSet',
  ONBOARDING_OWNER_NAME_SET: 'OnboardingOwnerNameSet',
  ONBOARDING_BUSINESS_PHONE_SET: 'OnboardingBusinessPhoneSet',
  ONBOARDING_ABOUT_YOU_FINISHED: 'OnboardingAboutYouFinished',
  ONBOARDING_PASSWORD_SET: 'OnboardingPasswordSet',
  ONBOARDING_B_LISTING_CLAIMED: 'OnboardingBListingClaimed',
  ONBOARDING_REFERRAL_CODE: 'OnboardingReferralCode',
  ONBOARDING_BUSINESS_CREATED: 'OnboardingBusinessCreated',
  ONBOARDING_WORKPLACE: 'OnboardingWorkplace',
  ONBOARDING_ADDRESS_SEARCH: 'OnboardingAddressSearch',
  ONBOARDING_ADDRESS: 'OnboardingAddress',
  ONBOARDING_MAP_LOCATION: 'OnboardingMapLocation',
  ONBOARDING_EDIT_LOCATION_PIN: 'OnboardingEditLocationPin',
  ONBOARDING_TRAVEL_FEE: 'OnboardingTravelFee',
  ONBOARDING_BUSINESS_HOURS: 'OnboardingBusinessHours',
  ONBOARDING_BUSINESS_REGISTRATION_CATEGORY_SELECTED:
    'OnboardingBusinessRegistrationCategorySelected',
  ONBOARDING_SERVICE: 'OnboardingService',
  ONBOARDING_SERVICES_CONCIERGE: 'OnboardingServicesConcierge',
  ONBOARDING_SERVICES_ADD_MORE: 'OnboardingServicesAddMore',
  ONBOARDING_BUSINESS_REGISTRATION_COMPLETED:
    'OnboardingBusinessRegistrationCompleted',
  ONBOARDING_RESOLVED: 'OnboardingResolved',
  ONBOARDING_ENTER_YOUR_ADDRESS: 'OnboardingEnterYourAddress',
  ONBOARDING_DELAY_SET: 'OnboardingDelaySet',
  BUSINESS_ONBOARDING_ACTION: 'BusinessOnboardingAction',
  AIR_ONBOARDING_ACTION: 'AirOnboardingAction',
  AIR_CALL_LOGS_ACTION: 'AirCallLogsAction',

  SIMPLIFIED_REFERRAL_OPENED: 'SimplifiedReferralOpened',
  SIMPLIFIED_REFERRAL_CODE_CLICKED: 'SimplifiedReferralCodeClicked',
  SIMPLIFIED_REFERRAL_SHARE_CLICKED: 'SimplifiedReferralShareClicked',
  OPEN_REFERRAL: 'open-referral',

  // subscription
  SUBSCRIPTION_CLICKED_ON_SETTINGS_LIST: 'SubscriptionClickedOnSettingsList',
  SUBSCRIPTION_SECTION_ENTERED: 'SubscriptionSectionEntered',

  SUBSCRIPTION_OFFER_DISPLAYED: 'SubscriptionOfferDisplayed',
  SUBSCRIPTION_OFFER_SUBSCRIBE_CLICKED: 'SubscriptionOfferSubscribeClicked',
  SUBSCRIPTION_OFFER_SUBSCRIBE_SUCCESS: 'SubscriptionOfferSubscribeSuccess',
  SUBSCRIPTION_OFFER_SUBSCRIBE_FAILED: 'SubscriptionOfferSubscribeFailed',

  SUBSCRIPTION_PAYMENT_DETAILS_ACTION: 'SubscriptionPaymentDetailsAction',

  SUBSCRIPTION_CHOOSE_PLAN_CLICKED: 'SubscriptionChoosePlanClicked',
  SUBSCRIPTION_PLAN_SELECTED: 'SubscriptionPlanSelected',

  SUBSCRIPTION_YOUR_SUBSCRIPTION_OPENED: 'SubscriptionYourSubscriptionOpened',

  SUBSCRIPTION_RENEWED: 'SubscriptionRenewed',
  SUBSCRIPTION_NOT_RENEWED: 'SubscriptionNotRenewed',

  SUBSCRIPTION_PAYMENT_METHOD_OPENED: 'SubscriptionPaymentMethodOpened',
  SUBSCRIPTION_PAYMENT_METHOD_SAVED: 'SubscriptionPaymentMethodSaved',
  SUBSCRIPTION_UPDATE_PAYMENT_METHOD_CLICKED:
    'SubscriptionUpdatePaymentMethodClicked',

  SUBSCRIPTION_TRIAL_WILL_EXPIRE_SHOWN: 'SubscriptionTrialWillExpireShown',
  SUBSCRIPTION_TRIAL_HAS_EXPIRED_SHOWN: 'SubscriptionTrialHasExpiredShown',
  SUBSCRIPTION_TRIAL_ACTION: 'SubscriptionTrialAction',
  SUBSCRIPTION_TRIAL_SELECT_PLAN_CLICKED: 'SubscriptionTrialSelectPlanClicked',

  SUBSCRIPTION_BRAINTREE_ERROR: 'SubscriptionBraintreeError',
  SUBSCRIPTION_BRAINTREE_TOKENIZE_ERROR: 'SubscriptionBraintreeTokenizeError',
  SUBSCRIPTION_BRAINTREE_3DS_VERIFY_ERROR:
    'SubscriptionBraintree3DSVerfifyError',

  SUBSCRIPTION_YOUR_SUBSCRIPTION_ACTION: 'SubscriptionYourSubscriptionAction',

  SUBSCRIPTION_YOUR_NEXT_PAYMENT_ACTION: 'SubscriptionYourNextPaymentAction',
  SUBSCRIPTION_YOUR_BILLING_ACTION: 'SubscriptionYourBillingAction',

  SUBSCRIPTION_ACCOUNT_DISABLED_ACTION: 'SubscriptionAccountDisabledAction',
  SUBSCRIPTION_PAYMENT_FAILED_ACTION: 'SubscriptionPaymentFailedAction',
  SUBSCRIPTION_TRIAL_WILL_EXPIRE_ACTION: 'SubscriptionTrialWillExpireAction',
  SUBSCRIPTION_ACCOUNT_DISABLED_RENEW_ACTION:
    'SubscriptionAccountDisabledRenewAction',
  SUBSCRIPTION_PAYMENT_METHOD_ACTION: 'SubscriptionpaymentMethodAction',

  SUBSCRIPTION_PAYMENT_HISTORY_ACTION: 'SubscriptionPaymentHistoryAction',
  SUBSCRIPTION_RENEW_YOUR_SUBSCRIPTION_ACTION:
    'SubscriptionRenewYourSubscriptionAction',

  BUSINESS_BILLING_ACTION: 'BusinessBillingAction',

  // offline migration
  // subscription_offline_migration
  OFFLINE_MIGRATION_MESSAGE: 'OfflineMigrationMessage',

  // join_subscription_online
  OFFLINE_MIGRATION_OFFER: 'OfflineMigrationOffer',

  // Subscription_Online_Failed
  OFFLINE_MIGRATION_OFFER_ACCEPTED: 'OfflineMigrationOfferAccepted',

  // price_information
  OFFLINE_MIGRATION_PRICE_INFO: 'OfflineMigrationPriceInfo',

  // dont_go_yet
  OFFLINE_MIGRATION_EXIT: 'OfflineMigrationExit',

  // confirm_and_ready
  OFFLINE_MIGRATION_AGREEMENTS: 'OfflineMigrationAgreements',

  // privacy_policy
  OFFLINE_MIGRATION_TERMS: 'OfflineMigrationTerms',

  // subscription_online_ready
  OFFLINE_MIGRATION_SUCCESS: 'OfflineMigrationSuccess',

  // subscription_online_failed
  OFFLINE_MIGRATION_ERROR: 'OfflineMigrationError',

  // subscription_offline_migration_reminder
  OFFLINE_MIGRATION_REMINDER: 'OfflineMigrationReminder',
  OFFLINE_MIGRATION_PAY_BY_CARD_INFO: 'OfflineMigrationPayByCardInfo',

  // Booksy Card Reader
  BCR_STARTED: 'BCRStarted',
  BCR_VERIFY: 'BCRVerify',
  BCR_STRIPE_KYC_STARTED: 'BCRStripeKYCStarted',
  BCR_ORDER_TERMINAL_STARTED: 'BCROrderTerminalStarted',
  BCR_BUY_SUBSCRIPTION_FROM_BCR: 'BCRBuySubscriptionFromBCR',
  BCR_BACK_TO_PAYMENT_TYPES: 'BCRBackToPaymentTypes',
  BCR_RESET_ACCOUNT_STARTED: 'BCRResetAccountStarted',
  BCR_BACK_TO_BOOKSY_CARD_READER: 'BCRBackToBooksyCardReader',
  BCR_UPDATE_INFORMATION_WHEN_PENDING: 'BCRUpdateInformationWhenPending',
  BCR_UPDATE_INFORMATION_WHEN_NOT_VERIFIED:
    'BCRUpdateInformationWhenNotVerified',
  BCR_HOW_IT_WORKS: 'BCRHowItWorks',
  BCR_LEARN_MORE: 'BCRLearnMore',
  BCR_CONTACT_US: 'BCRContactUs',
  BCR_CONNECTED_ACCOUNT_AGREEMENT: 'BCRConnectedAccountAgreement',
  BCR_CARD_READER_CONNECTION_IN_PROGRESS: 'BCRCardReaderConnectionInProgress',
  BCR_CARD_READER_CONNECT: 'BCRCardReaderConnect',
  // This is new event name for all new BCR firebase events
  BOOKSY_CARD_READER_ACTION: 'BCRCardReaderAction',

  // best of booksy
  SPC_BOB_EXPLANATION_ACTION: 'SPCBobExplanationAction',

  // social post creator
  SOCIAL_POST_CREATOR_ACTION: 'SocialPostCreatorAction',
  SOCIAL_POST_SHARED: 'SocialPostShared',
  POST_CREATOR_DOWNLOAD: 'post-creator.download',
  POST_CREATOR_SHARE: 'post-creator.share',

  // combo services
  BSC_COMBO_SERVICE_ACTION: 'BSCComboServiceAction',

  // business settings
  BIZ_SETTINGS_ACTION: 'BizSettingsAction',

  // checkout
  SPLASH_SCREEN_ACTION: 'SplashScreenAction',
  CHECKOUT_ACTION: 'CheckoutAction',

  // invite clients
  BUSINESS_INVITE_SPLASH_ACTION: 'BusinessInviteSplashAction',

  // turn tracker
  TURN_TRACKER_ACTION: 'TurnTrackerAction',

  // language settings
  LANGUAGE_SETTINGS_ACTION: 'LanguageSettingsAction',

  // calnedar settings
  CALENDAR_SETINGS_ACTION: 'CalendarSettingsAction',

  // experiments
  EXPERIMENT_ACTION: 'ExperimentAction',

  // services
  SERVICES_ACTION: 'ServicesAction',

  // stats and reports
  BUSINESS_REPORT_ACTION: 'BusinessReportAction',

  // hints
  BUSINESS_HINTS_ACTION: 'BusinessHintsAction',

  // appointments
  BUSINESS_BOOKING_ACTION: 'BusinessBookingAction',
  APPOINTMENT_ADDED: 'AppointmentAdded',
  CUSTOMER_APPOINTMENT_CHANGE_STATUS_NO_SHOW:
    'CustomerAppointmentChangeStatusNoShow',
  BUSINESS_APPOINTMENT_CHANGE_STATUS_NO_SHOW:
    'BusinessAppointmentChangeStatusNoShow',
  APPOINTMENT_VIEW_ACTION: 'AppointmentViewAction',

  // side menu
  BUSINESS_MAIN_TAB_ACTION: 'BusinessMainTabAction',

  // portfolio
  BUSINESS_PORTFOLIO_ACTION: 'BusinessPortfolioAction',

  // used in business store module
  BUSINESS_STATE_CHANGE: 'business.state-change',

  // marketing tab events
  BUSINESS_MARKETING_ACTION: 'BusinessMarketingAction',

  // referral events
  BUSINESS_REFERRAL_ACTION: 'BusinessReferralAction',

  // message blasts events
  BUSINESS_MESSAGE_BLASTS_ACTION: 'BusinessMessageBlastsAction',

  // InTouch Chat
  BUSINESS_IN_TOUCH_CHAT_ACTION: 'Business_In_Touch_Chat_Action',

  // promotions actions
  BUSINESS_PROMOTIONS_ACTION: 'BusinessPromotionsActions',

  // online booking
  ONLINE_BOOKING_ACTION: 'OnlineBookingAction',

  // boost
  BOOST_ACTION: 'BoostAction',

  API_ACCESS_TOKEN_ERROR: 'api:access-token-error',

  // gift cards
  GIFT_CARD_ACTION: 'GiftCardAction',

  // memberships (voucher templates; Confluence: Membership_Action)
  MEMBERSHIP_ACTION: 'MembershipAction',

  // payments
  BUSINESS_PAYMENTS_ACTION: 'BusinessPaymentsAction',

  // marketing banners
  MARKETING_BANNER_ACTION: 'MarketingBannerAction',

  // in-app business notifications (booksy_notification)
  BOOKSY_NOTIFICATION_ACTION: 'BooksyNotificationAction',
  WRAPPED_ACTION: 'Wrapped_Action',

  // fake door
  FAKE_DOOR_ACTION: 'FakeDoorAction',

  // ecommerce
  ECOMMERCE_ACTION: 'EcommerceAction',
  ECOMMERCE_STORE_ACTION: 'StoreAction',

  // open link
  OPEN_LINK: 'OpenLink',

  // eppo experiment
  EPPO_EXPERIMENT_ASSIGNMENT: 'EppoExperimentAssignment',

  CS_CHAT_CLICKED: 'CSChatClicked',

  LOGIN_LOGOUT_ACTION: 'LoginLogoutAction',

  ECOMMERCE_ROUTE_TO_ENTRYPOINT: 'EcommerceRouteToEntrypoint',

  INVENTORY_ROUTE_TO_ENTRYPOINT: 'InventoryRouteToEntrypoint',

  NO_SHOW_PROTECTION_ACTION: 'NoShowProtectionAction',

  BOOKSY_WALLET_ACTION: 'Booksy_Wallet_Action',

  GOOGLE_BUSINESS_PROFILE_ACTION: 'Google_Business_Profile_Action',

  PROVIDER_TEMPORARY_ACTION: 'Provider_Temporary_Action',

  BUSINESS_IMPORT_INVITE_ACTION: 'Business_Import_Invite_Action',

  BUSINESS_CUSTOMER_INFO_ACTION: 'Business_Customer_Info_Action',

  CALENDAR_WAITLIST_ACTION: 'CalendarWaitlistAction',

  REFUND_ACTION: 'Refund_Action',

  LOYALTY_CARD_ACTION: 'Loyalty_Card_Action',

  // Booksy Pay activation event for marketing
  BOOKSY_PAY_ENABLED: 'Booksy_Pay_Enabled',
}

export const PAY_BY_APP_STATUSES = {
  DISABLED: 'D',
  PENDING: 'P',
  PENDING_ADYEN_APPROVAL: 'F',
  DISABLED_DUE_TO_KYC_ERRORS: 'G',
  RESTRICTED_ACCESS: 'H',
  ENABLED: 'E',
}

export const WEBVIEW_TYPES = {
  IOS: 'ios',
  ANDROID: 'android',
  WWW: 'www',
}

export const APP_TYPE = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  PWA: 'pwa',
}

export const APP_VERSION = '3.0.0'

export const WEBVIEW_EVENTS = {
  LOGOUT: 'logout',
  SETTINGS_BOOKSY_OPEN: 'settings.booksy.open',
  BLUETOOTH_ENABLED: 'bluetooth.enabled',
  BLUETOOTH_TOGGLE: 'bluetooth.toggle',
  LOCATION_ENABLED: 'location.enabled',
  LOCATION_PERMISSIONS: 'location.permissions',
  BCR_CONNECTION_TOKEN: 'bcr.connectionToken',
  BCR_DISCOVER_START: 'bcr.readers.discover.start',
  BCR_DISCOVER_LIST: 'bcr.readers.discover.list',
  BCR_DISCOVER_CANCEL: 'bcr.readers.discover.cancel',
  BCR_CONNECT: 'bcr.readers.connect',
  BCR_CONNECTION_STATUS_CHANGE: 'bcr.readers.connectionStatusChange',
  BCR_DISCONNECT: 'bcr.readers.disconnect',
  BCR_UPDATE_AVAILABLE: 'bcr.update.isAvailable',
  BCR_UPDATE_START: 'bcr.update.start',
  BCR_UPDATE_STATUS: 'bcr.update.status',
  BCR_UPDATE_DONE: 'bcr.update.done',
  BCR_UNEXPECTED_DISCONNECT: 'bcr.readers.disconnected.unexpected',
  BCR_TRANSACTION_STATUS: 'bcr.transaction.status',
  BCR_TRANSACTION_START: 'bcr.transaction.start',
  BCR_TRANSACTION_PROCESS: 'bcr.transaction.processPaymentStarted',
  BCR_TRANSACTION_CANCEL: 'bcr.transaction.cancelCollectPaymentMethod',
  // rich text editor entrypoint for mobiles - TMP solution before microservice migration
  RICH_TEXT_READY: 'richText.ready',
  RICH_TEXT_EDITED: 'richText.edited',
  // mobile consents (showed in webView)
  CONSENT_AGREE: 'consent.agree',
  CONSENT_CANCEL: 'consent.cancel',
  CONSENT_DONE: 'consent.done',
  CONSENT_READY: 'consent.ready',
  CONSENT_DISCLAIMER_CLICK: 'consent.disclaimerClick',
  // open store
  ECOMMERCE_OPEN: 'ecommerce.store.openUrl',
  BROWSER_OPEN: 'browser.open',
}

export const STRIPE_FLOW = {
  AUTHORIZE_WITH_CARD: 'C', // setup intent
  AUTHORIZE_WITH_TRANSACTION: 'T', // payment intent
}
export const PARTNER_APP_EVENTS = {
  BACK: 'partnerApp.back',
  CREATED: 'partnerApp.created',
  INSTALLED: 'partnerApp.installed',
  REDIRECT_TO_APPOINTMENT: 'partnerApp.redirectToAppointment',
  REDIRECT_TO_PARTNER_APP_SETTINGS: 'partnerApp.redirectToPartnerAppSettings',
}

// subscription/billing braintree payment types
export const PAYMENT_METHOD = {
  CARD: 'C',
  PAYPAL: 'P',
  VENMO: 'V',
  UNKNOWN: 'unknown',
}

export const INVOICE_TYPES = {
  VAT: 'VAT',
  SALES: 'sales',
  REGULAR: 'regular',
}

export const DOCUMENT_SOURCES = {
  NAVISION: 'NAVISION',
  BILLING: 'BILLING',
  EXTERNAL: 'EXTERNAL',
}

// there are two types of subscription: braintree subscription (old); `booksy subscription` (new)
export const BOOKSY_SUBSCRIPTION_STATUS = {
  ACTIVE: 'A',
  PENDING: 'P',
  BLOCKED: 'B',
  SUSPENDED: 'S',
  SUSPENSION_PENDING: 'U',
}

export const INVITE_STATUSES = {
  NO_BOOKINGS: 'no_bookings',
  RECENTLY_INVITED: 'recently_invited',
  NO_RESPOND: 'no_respond',
  NOT_INVITED: 'not_invited',
  INVITE_PENDING: 'invite_pending',
  ALREADY_BOOKED: 'already_booked',
}

export const STORIES_DRAWER_HINT_KEYS = {
  STORY_FEATURE_SHIFTWORKS: 'storyFeatureShiftworks',
  STORY_FEATURE_COMMISSIONS: 'storyFeatureCommissions',
  STORY_FEATURE_INVENTORY: 'storyFeatureInventory',
  STORY_FEATURE_SOCIAL_POST_CREATOR: 'storyFeatureSocialPostCreator',
  STORY_FEATURE_PROMOTIONS: 'storyFeaturePromotions',
  STORY_FEATURE_PROMOTIONS_WITH_PEAK_HOURS:
    'storyFeaturePromotionsWithPeakHours',
  STORY_FEATURE_FLASH_SALES: 'storyFeatureFlashSales',
  STORY_FEATURE_HAPPY_HOURS: 'storyFeatureHappyHours',
  STORY_FEATURE_PEAK_HOURS: 'storyFeaturePeakHours',
  STORY_FEATURE_LAST_MINUTE_DISCOUNT: 'storyFeatureLastMinuteDiscount',
  STORY_FEATURE_GIFT_CARDS: 'storyFeatureGiftCards',
  STORY_FEATURE_MEMEBERSHIPS: 'storyFeatureMemberships',
  STORY_MESSAGE_BLASTS: 'storyMessageBlasts',
  STORY_COMBO_SERVICES: 'storyComboServices',
  STORY_FEATURE_LOYALTY_PROGRAM: 'storyFeatureLoyaltyProgram',
  STORY_IMPORT_AND_INVITE: 'storyFeatureImportAndInvite',
  STORY_INVITE_AGAIN: 'storyFeatureInviteAgain',
  STORY_QUICK_INVITE: 'storyFeatureQuickInvite',
  STORY_ECOMMERCE: 'storyFeatureEcommerce',
  STORY_MY_ORDERS: 'storyFeatureMyOrders',
}

// this enum has replaced `business.promoted` flag
export const BUSINESS_BOOST_STATUSES = {
  ENABLED: 'E',
  DISABLED: 'D',
  ACTIVATION_PENDING: 'A',
  SUSPENSION_PENDING: 'S',
}

export const BOOST_STATUSES = {
  ACTIVE: 'active',
  BLOCKED: 'blocked',
  DEACTIVATED: 'deactivated',
  DISABLED: 'disabled',
  INACTIVE: 'inactive',
  NOT_ELIGIBLE: 'not_eligible',
  SUSPENSION_PENDING: 'suspension_pending',
}

export const BOOST_STATUS_LABELS = {
  [BOOST_STATUSES.ACTIVE]: __t('components.promotion.boost.status.active'),
  [BOOST_STATUSES.BLOCKED]: __t('components.promotion.boost.status.blocked'),
  [BOOST_STATUSES.DEACTIVATED]: __t(
    'components.promotion.boost.status.deactivated'
  ),
  [BOOST_STATUSES.DISABLED]: __t('components.promotion.boost.status.disabled'),
  [BOOST_STATUSES.INACTIVE]: __t('components.promotion.boost.status.inactive'),
  [BOOST_STATUSES.NOT_ELIGIBLE]: __t(
    'components.promotion.boost.status.not-eligible'
  ),
  [BOOST_STATUSES.SUSPENSION_PENDING]: __t(
    'components.promotion.boost.status.suspension-pending'
  ),
}

export const BOOST_PAYMENT_SOURCE = {
  OFFLINE: 'O',
  ONLINE: 'B',
}

export const NOTIFICATION_TYPES = {
  BOOKING: 'booking',
  SHOW_APPOINTMENT: 'show_appointment',
  AGENDA: 'agenda',
  CUSTOMERS: 'customers',
  PROFILE: 'profile',
  PROFILE_COMPLETENESS: 'profile_completeness',
  SUBDOMAIN_SHARE: 'subdomain_share',
  REVIEW: 'review',
  REVIEW_LIST: 'review_list',
  PORTFOLIO_PHOTO: 'portfolio_photo',
  PORTFOLIO: 'portfolio',
  REFERRAL: 'referral',
  GIFT_CARDS: 'gift_cards',
  SOLD_GIFT_CARDS: 'sold_gift_cards',
  PROFILE_STATISTICS: 'profile_statistics',
  FLASH_SALE: 'flash_sale',
  HAPPY_HOURS: 'happy_hours',
  LAST_MINUTE: 'last_minute',
  BOOST_GENERIC: 'boost_generic',
  BOOST_VISIBILITY: 'boost_visibility',
  BOOST_PROFILE: 'boost_profile',
  INVITE_CUSTOMERS: 'invite_customers',
  DIGITAL_FLYER: 'digital_flyer',
  SHARE: 'share',
  BLAST_DASHBOARD: 'blast_dashboard',
  BLAST_TEMPLATE: 'blast_template',
  NO_SHOW_PROTECTION: 'no_show_protection',
  PROFILE_PHOTOS: 'profile_photos',
  CALENDAR: 'calendar',
  CALENDAR_IMPORTER: 'calendar_importer',
  MARKETING_HAPPY_HOURS: 'marketing.happy_hours',
  MARKETING_LAST_MINUTE: 'marketing.last_minute',
  MARKETING_FLASH_SALE: 'marketing.flash_sale',
  BOOST_DASHBOARD: 'boost_dashboard',
  BUSINESS_SAFETY_RULES: 'business_safety_rules',
  TRIAL_END: 'trial_end',
  TRIAL_END_BLOCKED: 'trial_end_blocked',
  ENABLE_NO_SHOW_PROTECTION: 'enable_no_show_protection',
  KYC_NOTIFICATION: 'kyc_notification',
  FEEDBACK: 'feedback',
  REFERRAL_KYC: 'referral_kyc',
  PREPAYMENT_POPUP: 'prepayment_popup',
  STRIPE_DASHBOARD: 'stripe_dashboard',
  KYC_DASHBOARD: 'kyc_dashboard',
  KYC_DASHBOARD_STRIPE: 'kyc_dashboard_stripe',
  DEEPLINK: 'deeplink',
  TRANSACTION: 'transaction',
  BOOKSY_AWARDS: 'booksy_awards',
  BOOKING_TOOLS: 'booking_tools',
  PARTNER_TYPE_ACTION: 'partner_app_action',
  PARTNER_TYPE_SETUP: 'partner_app_setup',
  PARTNER_TYPE_DASHBOARD: 'partner_app_dashboard',
  BOOST_BAN: 'boost_ban',
  FAKE_DOOR: 'fake_door',
  COVER_PHOTO: 'cover_photo',
  IMPORT_AND_INVITE: 'import_and_invite',
  STORE: 'store',
  GOOGLE_BUSINESS_PROFILE: 'google_business_profile',
  BUY_BOOKSY_MED: 'buy_booksy_med',
  LOYALTY_PROGRAM: 'loyalty_program',
  ONLINE_BOOKING_INSTAGRAM: 'online_booking_instagram',
  SUBSCRIPTIONS: 'subscriptions',
  BOOKSY_WALLET: 'booksy_wallet',
  BOOKSY_PAY_ACTIVATION: 'booksy_pay_activation',
  BOOST_SCORE: 'boost_score',
}

export const MOBILE_LINKS = {
  GOOGLE_PLAY_PAYMENTS: 'https://play.google.com/store/paymentmethods',
  APPLE_ITUNES_PAYMENTS: 'https://apps.apple.com/account/billing',
}

export const PRODUCT_TYPES = {
  SAAS: 'SA',
  STAFFER_SAAS: 'SS',
  POSTPAID_SMS: 'PS',
  BUSY: 'BU', // Not used yet
  STAFFER_BUSY: 'SB', // Not used yet
  PREPAID_SMS: 'RS', // Not used yet
  BOOST: 'BO', // Not used yet
}

export const PRODUCT_TYPES_NAVISION = {
  ACTIVATION: 'Activation',
  FEE: 'fee',
  SAAS: 'SaaS',
  BOOST: 'Boost',
  STAFFERS: 'Staffers',
  SMS: 'SMS',
}

export const LOGIN_TYPES = {
  ACCESS_TOKEN: 'access_token',
  AUTH_TOKEN: 'auth_token',
  APPLE: 'apple',
  DEFAULT: 'default',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
}

export const POS_PLAN_TYPE = {
  ADYEN_MOBILE_PAYMENT: 'adyen_mobile_payment',
  STRIPE_MOBILE_PAYMENT: 'stripe_mobile_payment',
  ADYEN_DONATIONS: 'adyen_donations',
  FAST_PAYOUT: 'fast_payout',
  STRIPE_TERMINAL: 'stripe_terminal',
  TAP_TO_PAY: 'tap_to_pay',
  BOOKSY_PAY: 'booksy_pay',
}

export const POS_TRANSACTIONS_SCOPE = {
  DAY: 'day',
  MONTH: 'month',
  CUSTOM: 'custom',
}

export const IMAGE_CATEGORY = {
  WAREHOUSE_BRAND_PHOTOS: 'warehouse_brand_photos',
  BIZ_PHOTO: 'biz_photo',
  DF_BACKGROUND: 'df_background',
  BOOST_BACKGROUND: 'boost_background',
  MESSAGE_BLAST_PHOTOS: 'message_blast_photos',
  DF_GROUP_BACKGROUND: 'df_group_background',
  DIGITAL_FLYER_FB: 'digital_flyer_fb',
  MARKETING: 'marketing',
  LOGO: 'logo',
  WAREHOUSE_COMMODITY_PHOTOS: 'warehouse_commodity_photos',
  MESSAGE_BLAST_PHOTO: 'message_blast_photo',
  HINT_AVATAR: 'hint_avatar',
  INSPIRATION: 'inspiration',
  DF_CATEGORY_BACKGROUND: 'df_category_background',
  DIGITAL_FLYER: 'digital_flyer',
  DF_RENDER: 'df_render',
}

export const NO_SHOW_PROTECTION = {
  OFF: 'OF',
  CANCELLATION_FEE: 'CF',
  PREPAYMENT: 'PP',
}

export const DEFAULT_LOAD_MORE_DELTA = 10 // [pixels]

export const CONNECTION_PROBLEMS = {
  NO_NETWORK_CONNECTION: 'no-network-connection',
  NO_SERVER_CONNECTION: 'no-server-connection',
}

export const DISCOUNT_TYPES = {
  FIXED: 'F',
  PERCENTAGE: 'P',
  NONE: 'X',
}

export const AUTH_ORIGIN = {
  APPLE: 'A',
  BOOKSY: 'B',
  FACEBOOK: 'F',
  GOOGLE: 'G',
}

export const BUSINESS_CATEGORY_INTERNAL_NAMES = {
  AESTHETIC_MEDICINE: 'Aesthetic medicine',
  AUTOMOTIVE: 'Automotive',
  BARBERS: 'Barbers',
  BRAIDS: 'Braids',
  BROWS_AND_LASHES: 'Brows & Lashes',
  CHIROPRACTIC: 'Chiropractic',
  DAY_SPA: 'Day SPA',
  DENTAL: 'Dental',
  DIETICIAN: 'Dietician',
  FINANCIAL_INSTITUTIONS: 'Financial Institutions',
  HAIR_REMOVAL: 'Hair Removal',
  HAIR_SALONS: 'Hair salons',
  HEALTH: 'Health',
  HEALTH_AND_WELLNESS: 'Health & Wellness',
  HOLISTIC_MEDICINE: 'Holistic Medicine',
  HOME_SERVICES: 'Home services',
  KOMFORT: 'Komfort',
  MAKE_UP: 'Make-up',
  MASSAGE: 'Massage',
  NAIL_SALONS: 'Nail salons',
  OTHER: 'Other',
  PERSONAL_TRAINERS: 'Personal trainers',
  PET_SERVICES: 'Pet services',
  PHYSICAL_THERAPY: 'Physical Therapy',
  PIERCING: 'Piercing',
  PODIATRY: 'Podiatry',
  PROFESSIONAL_SERVICES: 'Professional Services',
  PSYCHOTHERAPY: 'Psychotherapy',
  SHOPPING: 'Shopping',
  SKIN_CARE: 'Skin care',
  TATOO_ARTISTS: 'Tattoo artists',
  TELECOM: 'Telecom',
  THERAPY: 'Therapy',
  WEDDING_MAKEUP_ARTIST: 'Wedding Makeup Artist',
  WEIGHT_LOSS: 'Weight Loss',
  WOSP: 'WOSP',
  YOGA: 'Yoga',
}

// Deprecated import keys
export {
  KEYS as LOCAL_STORAGE_KEYS,
  KEYS as SESSION_STORAGE_KEYS,
} from '@/composables/storage/const'

export const LEGAL_DOCUMENT_TYPE = {
  TERMS_OF_USE: 'TERMS_OF_USE',
  PRIVACY_POLICY: 'PRIVACY_POLICY',
  INFORMATION_OBLIGATION: 'INFORMATION_OBLIGATION',
  BOOST_TERMS: 'BOOST_TERMS',
  BILLING_TERMS: 'BILLING_TERMS',
}

export const PAYMENT_STATUS_ERRORS = {
  CONTACT_WITH_PAYPAL: 'contact_with_paypal',
  TRY_AGAIN: 'try_again',
  TRY_ANOTHER_METHOD: 'try_another_method',
  CONTACT_WITH_BANK: 'contact_with_bank',
  INSUFFICIENT_FUNDS: 'insufficient_funds',
  CONTACT_WITH_US: 'contact_with_us',
}

export const SERVICE_VARIANT_MODE = {
  VARIANT: 'variant',
  NO_VARIANT: 'no-variant',
}

export const HASHTAG_DATA_SOURCE = {
  CUSTOMER: 'CUSTOMER',
  DIGITAL_FLYER: 'DIGITAL_FLYER',
}

export const INCOME_TYPES = {
  GROSS: 'gross',
  NET: 'net',
}

export const BUSINESS_VISIBILITY_VALIDATION_ERROR_CODES = {
  INVITES: 'invites_error',
  LAST_AVAILABLE_SERVICE: 'last_available_service_error',
  LAST_AVAILABLE_STAFF: 'last_available_resource_error',
  LAST_SERVICE_STAFFER_DELETION: 'last_service_staffer_deletion_error',
  UPCOMING_APPOINTMENTS: 'conflict',
}

export const EXISTING_COMBO_VALIDATION_ERROR_CODES = {
  EXISTING: 'existing_dependent_combo',
  VARIANT_EXISTING: 'variant_existing_dependent_combo',
}

export const SERVICE_TYPE = {
  TRAVELING: 'traveling',
  OFFLINE: 'offline',
}

export const CUSTOMERS_MAX_DISPLAY_COUNT = 10000 // due to elastic search we can only provide up to 10000 count of customers in a single request

export const KYC_STATUSES = {
  TURNED_OFF: 'TURNED_OFF',
  VERIFICATION_PENDING: 'VERIFICATION_PENDING',
  NOT_VERIFIED: 'NOT_VERIFIED',
  VERIFIED: 'VERIFIED',
  BLOCKED: 'BLOCKED',
}

export const BVC_LABEL_COLOR_VARIANTS = {
  DEFAULT: 'Default',
  ORANGE: 'Orange',
  RED: 'Red',
  GREEN: 'Green',
  GRAY: 'Gray',
  BLUE: 'Blue',
  PURPLE: 'Purple',
  PINK: 'Pink',
  OUTLINED: 'Outlined',
  BLACK: 'Black',
  LIGHT: 'Light',
  TEAL: 'Teal',
}

export const BVC_BADGE_COLORS = {
  ORANGE: 'orange',
  RED: 'red',
  GREEN: 'green',
  GRAY: 'gray',
  VIOLET: 'violet',
  GRADIENT: 'gradient',
  TEAL: 'teal',
}

export const PAYMENT_PROCESSOR_URLS = {
  ADYEN_TERMS_URL: 'https://www.adyen.com/legal/terms-and-conditions',
  ADYEN_ACTIONS_URL: 'https://www.adyen.com/legal/list-restricted-prohibited',
  STRIPE_TERMS_URL: 'https://stripe.com/legal/connect-account',
}

export const OPTION_CHOSEN = {
  DAY_VIEW: 'day_view',
  TIME_OFF: 'time_off',
  WORKING_HOURS: 'working_hours',
}

export const SERVICE_COLOR_PALETTE = {
  VIVID: 'vivid',
  PASTEL: 'pastel',
}

export const PARTNER_APP_TYPES = {
  PHYSIOREGISTRATION: 'fizjorejestracja',
}

export const PARTNER_APP_DATA_KIND = {
  MEDICAL: 'medical',
}

export const PARTNER_APP_DATA_SYNC_STATUS = {
  INITIAL: 'initial',
  PENDING: 'pending',
  FINISHED: 'finished',
}

export const PARTNER_APP_CALL_TO_ACTION = {
  APPOINTMENT_DETAILS: 'appointment_details',
  DASHBOARD: 'dashboard',
  CUSTOMER_DETAILS: 'customer_details',
  RESOURCE_DETAILS: 'resource_details',
  SERVICE_DETAILS: 'service_details',
  SETTINGS: 'settings',
  SIDEBAR: 'sidebar',
}

export const PARTNER_APP_INTEGRATION_STATUSES = {
  ACTIVE: 'A',
  SUSPENDED: 'S',
  PAID: 'P',
  BLOCKED: 'B',
  DISABLED: 'D',
}

export const PARTNER_APP_ACTIVE_STATUSES = {
  ACTIVE: 'A',
  PAID: 'P',
  SUSPENDED: 'S',
  PENDING_CANCELLATION: 'E',
  OVERDUE_BLOCKED: 'O',
  CHURNED: 'C',
  TRIAL: 'T',
}

export const BOOKSY_MED_SUBSCRIPTION_STATUSES = [
  PARTNER_APP_ACTIVE_STATUSES.PAID,
  PARTNER_APP_ACTIVE_STATUSES.TRIAL,
  PARTNER_APP_ACTIVE_STATUSES.PENDING_CANCELLATION,
]

export const CLAIM_RESULT = {
  SENT: 'sent',
  ACCOUNT_NOTICE: 'account_notice',
  OVERDUE: 'overdue',
}

export const POLL_TEXT_MAX_LENGTH = 256

export const CLAIM_MAX_IMAGE_SIZE = 2 * 1000 * 1000 // 2MB
export const CLAIM_MAX_IMAGE_SIZE_MB = CLAIM_MAX_IMAGE_SIZE / 1000000
export const CLAIM_MAX_IMAGES = 3
export const CLAIM_MAX_RESOLUTION = 4096
export const CLAIM_IMAGE_TYPE_REGEXP = /image\/(?:jpg|jpeg|gif|png)$/

export const INSTAGRAM_BUSINESS_ACCOUNT_SUPPORT_URL = 'https://www.facebook.com/help/instagram/502981923235522'
export const IMAGES_FETCH_LIMIT = 20

export const MIGRATION_SOURCES = Object.freeze({
  VERSUM: 'Versum',
})

export const COST_ESTIMATION_TIMEOUT = 30 * 1000 // 30s

export const FEATURES_STATUS_TIMEOUT = 20 * 1000 // 20s

export const WAREHOUSE_DOCUMENTS_TIMEOUT = 20 * 1000 // 20s

export const BLAST_PREVIEW_TIMEOUT = 30 * 1000 // 30s

export const SINGLE_MESSAGE_TEMPLATE_TIMEOUT = 30 * 1000 // 30s

export const POST_TEMPLATE_TIMEOUT = 30 * 1000 // 30s

export const PAYMENT_ROWS_TIMEOUT = 60 * 1000 // 60s

export const DATADOG_CUSTOM_ATTRIBUTES = {
  BUSINESS_ID: 'business_id',
  USER_ID: 'user_id',
  SALON_SIZE: 'salon_size',
  CALENDAR_DISPLAYED_RESOURCES: 'calendar_displayed_resources',
  CALENDAR_DISPLAY_MODE: 'calendar_display_mode',
  BOOKSY_COUNTRY_CODE: 'booksy_country_code',
}

export const DATADOG_CUSTOM_ACTIONS_NAMES = {
  CALENDAR_REFRESH_LOADER: 'CalendarRefreshLoader',
  CALENDAR_FIRESTORE_UPDATE_ON_USER_ACTION_CHECKER: 'CalendarFirestoreUpdateOnUserActionChecker',
  CALENDAR_FIRESTORE_LACK_OF_UPDATES: 'CalendarFirestoreLackOfUpdates',
  BOOKSY_UNRESPONSIVE_MESSAGE: 'BooksyUnresponsiveMessage',
  LOGIN_WORKING_PROPERLY: 'LoginWorkingProperly',
  LOGIN_NOT_WORKING: 'LoginNotWorking',
  STARTUP_START: 'StartupStart',
  STARTUP_SUCCESS: 'StartupSuccess',
}

export const REDIRECTED_FROM = {
  PROFILE_COMPLETENESS: 'profile_completeness',
  WALKTHROUGHS: 'walkthroughs',
}

export const ANALYTICS_SOURCES = {
  PROFILE_SHARE_FROM_TARGET_TYPE: 'profile_share_from_target_type',
  PROFILE_SHARE_FROM_BOOST: 'profile_share_from_boost',
  PROFILE_SHARE_FROM_PROFILE: 'profile_share_from_profile',
  PROFILE_COPY_FROM_PROFILE: 'profile_copy_from_profile',
  PROFILE_SHARE_FROM_DEEPLINK: 'profile_share_from_deeplink',
  PROFILE_COPY_FROM_DEEPLINK: 'profile_copy_from_deeplink',
  PROFILE_SHARE_FROM_PROFILE_COMPLETENESS:
    'profile_share_from_profile_completeness',
  PROFILE_COPY_FROM_PROFILE_COMPLETENESS: 'profile_copy_from_profile_completeness',
}

export const SUBDOMAIN_SHARE_QUERY_TYPES = {
  DEEPLINK: '1',
  TARGET_TYPE: '2',
}

export const BOOST_BAN_TYPES = {
  SUSPENSION: 'suspension',
  SUSPENSION_WITH_INVOICE: 'suspension_with_invoice',
  TERMINATION: 'termination',
  TERMINATION_WITH_INVOICE: 'termination_with_invoice',
}

export const ONLINE_BOOKING_INTEGRATIONS = {
  INSTAGRAM: 'instagram',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  WEBSITE: 'website',
}

export const TOOLTIP_POSITIONS = [
  'right-top',
  'top-center',
  'bottom-center',
  'bottom-left',
  'top-left',
  'top-right',
  'right-center',
  'left-center',
  'left-bottom',
  'left-top',
]

export const FLASH_SALE_SETUP_CODES = {
  ACTIVATION_DISCOUNT: 'activation_discount',
}

export const PROFILE_TYPES = Object.freeze({
  BUSINESS: 'B',
  CUSTOMER: 'C',
})
